const FEDRAMP_EXPLANATIONS = {

  // ── ACCESS CONTROL (AC) ───────────────────────────────────────────────────

  'AC-1': {
    plain_english: 'You need a written access control policy that spells out who is responsible for managing access, and documented procedures that describe how to actually implement that policy.',
    example: 'A cloud service provider publishes an Access Control Policy document reviewed annually, with a companion SOP describing how accounts are provisioned and deprovisioned.',
    evidence_tips: [
      'Current signed/approved Access Control Policy document',
      'Procedures document with step-by-step account management instructions',
      'Evidence of annual review (change log, re-approval date)',
    ],
    common_mistakes: 'Having a policy document but no matching procedures, or letting the policy go unreviewed for more than a year.',
    nist_reference: 'AC-1',
  },

  'AC-2': {
    plain_english: 'The organization must formally manage all user accounts throughout their lifecycle — creating, modifying, reviewing, disabling, and removing accounts per defined rules.',
    example: 'An HR-triggered workflow creates a new employee account with manager approval, sets an expiry date for contractors, and automatically notifies IT when someone leaves.',
    evidence_tips: [
      'Account management procedures or runbooks',
      'Ticketing system records showing account creation/removal approvals',
      'Quarterly account review reports signed by account owners',
      'Automated offboarding workflow documentation',
    ],
    common_mistakes: 'Leaving accounts active after an employee departs or failing to document the account review process with dates and approver names.',
    nist_reference: 'AC-2',
  },

  'AC-3': {
    plain_english: 'The system must technically enforce who is allowed to access what — role-based or attribute-based controls must actually block unauthorized access, not just rely on policy.',
    example: 'A cloud storage bucket uses IAM policies so only the "data-analysts" role can read raw data files; developers cannot access production data without explicit approval.',
    evidence_tips: [
      'IAM policy or RBAC configuration screenshots',
      'Access matrix showing roles and their permitted resources',
      'Penetration test or access-control test results confirming enforcement',
    ],
    common_mistakes: 'Defining roles in documentation but not enforcing them in the system, or giving everyone broad admin-level access "just in case."',
    nist_reference: 'AC-3',
  },

  'AC-4': {
    plain_english: 'The system must control how information flows between internal components and external systems — preventing data from moving to unauthorized destinations.',
    example: 'A DMZ firewall enforces that sensitive database traffic can never flow directly to the internet; only the web tier can query the database, and only on approved ports.',
    evidence_tips: [
      'Network diagrams showing approved data flow paths',
      'Firewall rule sets or security group configurations',
      'Data flow analysis or DLP policy documentation',
    ],
    common_mistakes: 'Allowing unrestricted outbound connections that could exfiltrate sensitive data, or failing to document approved data flows.',
    nist_reference: 'AC-4',
  },

  'AC-5': {
    plain_english: 'No single person should be able to complete a sensitive end-to-end transaction alone — split duties so that fraud or mistakes require collusion.',
    example: 'The engineer who writes a deployment script cannot also approve it; a separate change-approval board must sign off before the script runs in production.',
    evidence_tips: [
      'Roles-and-responsibilities matrix showing separated duties',
      'Change management records requiring two distinct approvers',
      'Access control configuration proving developers lack production deployment rights',
    ],
    common_mistakes: 'Giving developers the ability to both write code and deploy directly to production without a second approver in the pipeline.',
    nist_reference: 'AC-5',
  },

  'AC-6': {
    plain_english: 'Users and processes should only have the minimum privileges needed to do their job — nothing more.',
    example: 'A billing service account has read-only access to the invoicing database and cannot write to other tables; no developer account has standing root access.',
    evidence_tips: [
      'Privilege review reports showing each account and its justification',
      'Evidence of privileged-access reviews conducted quarterly',
      'Configuration screenshots showing least-privilege role definitions',
    ],
    common_mistakes: 'Assigning "admin" to all technical staff as a convenience, or not re-evaluating permissions when someone changes roles.',
    nist_reference: 'AC-6',
  },

  'AC-7': {
    plain_english: 'After a defined number of failed login attempts (e.g., 3–5), the system must lock the account or introduce a delay to slow brute-force attacks.',
    example: 'After 5 consecutive failed logins, an account locks for 30 minutes and sends an alert email to the security team.',
    evidence_tips: [
      'Authentication system configuration showing lockout threshold and duration',
      'Test evidence demonstrating lockout behavior',
      'Security monitoring alert rules for repeated failed logins',
    ],
    common_mistakes: 'Setting the lockout threshold too high (e.g., 100 attempts) or not alerting when lockouts occur repeatedly.',
    nist_reference: 'AC-7',
  },

  'AC-8': {
    plain_english: 'Before anyone logs in, the system must display a warning banner that says the system is government-monitored and that continued use constitutes consent.',
    example: 'The login screen displays: "This is a U.S. Government information system. Unauthorized use is subject to criminal and civil penalties. All activity is monitored."',
    evidence_tips: [
      'Screenshot of the login page showing the approved banner text',
      'Banner configuration file or GPO setting',
      'ATO-approved banner wording in system documentation',
    ],
    common_mistakes: 'Omitting the banner entirely on SSH or API endpoints, or using informal wording that does not meet federal requirements.',
    nist_reference: 'AC-8',
  },

  'AC-9': {
    plain_english: 'After a successful login, the system should show the user when they last logged in and how many failed attempts occurred since then.',
    example: "A user's dashboard displays: \"Last successful login: June 10, 2026, 09:14 UTC. Failed attempts since last login: 0.\"",
    evidence_tips: [
      'Screenshot or demo showing last-login notification on login success',
      'Authentication system configuration enabling this feature',
    ],
    common_mistakes: 'Not surfacing last-login info to users, making it impossible for them to notice unauthorized use of their credentials.',
    nist_reference: 'AC-9',
  },

  'AC-10': {
    plain_english: 'The system must limit how many concurrent sessions a single account can have open at the same time.',
    example: 'An admin account is restricted to 3 simultaneous active sessions; a fourth login attempt from a new device is rejected until one session ends.',
    evidence_tips: [
      'Session management configuration showing concurrent-session limit',
      'Test evidence showing rejection of excess sessions',
    ],
    common_mistakes: 'Not configuring any concurrent-session limit, allowing credentials to be shared across many simultaneous users.',
    nist_reference: 'AC-10',
  },

  'AC-11': {
    plain_english: 'Sessions must automatically lock after a period of inactivity, requiring re-authentication before continuing.',
    example: 'An administrative console locks after 15 minutes of inactivity, displaying a locked screen that requires the user\'s password or MFA to resume.',
    evidence_tips: [
      'System or OS configuration showing idle timeout and lock settings',
      'Screenshot of the lock screen triggered by inactivity',
    ],
    common_mistakes: 'Setting excessively long timeouts (e.g., 8 hours) or only applying locks to desktop clients but not web-based admin consoles.',
    nist_reference: 'AC-11',
  },

  'AC-12': {
    plain_english: 'The system must automatically end a user session after a defined period or when certain conditions are met, not just lock it.',
    example: 'All web sessions expire and terminate after 30 minutes of inactivity, requiring full re-authentication rather than just unlocking.',
    evidence_tips: [
      'Session timeout configuration in the application or middleware',
      'Evidence of session expiry in access logs',
    ],
    common_mistakes: 'Confusing session lock (AC-11) with session termination — termination ends the session entirely and requires a full new login.',
    nist_reference: 'AC-12',
  },

  'AC-14': {
    plain_english: 'The organization must identify and document the specific actions users can take without authenticating (e.g., viewing a public FAQ), ensuring this list is intentional and minimal.',
    example: 'A documented list states that only the public marketing pages and the password-reset request page are accessible without authentication; everything else requires login.',
    evidence_tips: [
      'Documented list of unauthenticated actions and their business justification',
      'Architecture diagram showing which endpoints are publicly accessible',
    ],
    common_mistakes: 'Not formally documenting unauthenticated access points, leaving open endpoints that weren\'t intentional.',
    nist_reference: 'AC-14',
  },

  'AC-16': {
    plain_english: 'The system must associate and maintain security or privacy labels/attributes with data as it is stored, processed, and transmitted.',
    example: 'Files tagged as "CUI" retain that classification label in metadata whether stored in S3, transmitted over HTTPS, or processed by a Lambda function.',
    evidence_tips: [
      'Data classification schema and labeling policy',
      'Technical demonstration of attribute persistence in storage and transit',
      'DLP or tagging tool configuration',
    ],
    common_mistakes: 'Applying labels only at the point of creation but not carrying them through processing pipelines or transmission.',
    nist_reference: 'AC-16',
  },

  'AC-17': {
    plain_english: 'Remote access must be authorized, documented, and governed by defined usage restrictions and configuration requirements before anyone connects.',
    example: 'The remote access policy requires VPN with MFA, prohibits split tunneling, and mandates that only managed devices may connect; all remote sessions are logged.',
    evidence_tips: [
      'Remote access policy and approval records',
      'VPN/Zero Trust configuration screenshots showing required settings',
      'Audit logs showing remote access sessions',
    ],
    common_mistakes: 'Allowing ad-hoc remote access tools (personal VPNs, consumer RDP) without formal authorization or logging.',
    nist_reference: 'AC-17',
  },

  'AC-18': {
    plain_english: 'Wireless access must be formally authorized and governed — rogue or unmanaged Wi-Fi on organization premises is prohibited.',
    example: 'Only corporate-managed Wi-Fi SSIDs with WPA3-Enterprise and 802.1X authentication are allowed; wireless scanning is performed quarterly for rogue access points.',
    evidence_tips: [
      'Wireless access policy with approved SSIDs and authentication requirements',
      'Wireless controller configuration exports',
      'Rogue AP scan results',
    ],
    common_mistakes: 'Allowing employees to set up personal hotspots or consumer routers on the corporate network without documentation or controls.',
    nist_reference: 'AC-18',
  },

  'AC-19': {
    plain_english: 'Mobile devices (phones, tablets, laptops) must be authorized and governed by documented configuration and connection requirements before accessing organizational systems.',
    example: 'An MDM solution enforces full-disk encryption, screen lock, and remote-wipe on all mobile devices that access corporate email or the cloud environment.',
    evidence_tips: [
      'Mobile device policy and MDM enrollment records',
      'MDM configuration profile showing enforced settings',
      'List of authorized devices registered in MDM',
    ],
    common_mistakes: 'Allowing BYOD access without MDM enrollment or without defining minimum security requirements for personal devices.',
    nist_reference: 'AC-19',
  },

  'AC-20': {
    plain_english: 'When employees use external or personally owned systems to access organizational resources, formal terms and conditions must define what is and isn\'t allowed.',
    example: 'Contractors sign an Acceptable Use Agreement before accessing the cloud portal from personal laptops; the agreement prohibits storing CUI locally and requires updated AV.',
    evidence_tips: [
      'Terms and conditions or AUA documents with signatures',
      'External system authorization records',
      'Technical controls limiting what external systems can access',
    ],
    common_mistakes: 'Letting third parties connect to systems without signed agreements or without verifying their device meets minimum security requirements.',
    nist_reference: 'AC-20',
  },

  'AC-21': {
    plain_english: 'Before sharing information with an external partner, authorized users must be able to verify that the partner\'s access authorizations match the data\'s sensitivity level.',
    example: 'Before emailing a CUI document to a partner agency, a user checks the data-sharing agreement to confirm the recipient is cleared to receive that classification.',
    evidence_tips: [
      'Information-sharing procedures with verification steps',
      'Data-sharing agreements listing authorized recipients and classification levels',
      'Training records showing users understand sharing rules',
    ],
    common_mistakes: 'Sharing data based on informal email requests without verifying the recipient\'s authorization level in a formal agreement.',
    nist_reference: 'AC-21',
  },

  'AC-22': {
    plain_english: 'The organization must designate who is authorized to post publicly, train them on avoiding unauthorized disclosure, and review posted content for compliance.',
    example: 'A content policy designates the Communications team as the only group authorized to post on the public website; all new content is reviewed by Legal before publishing.',
    evidence_tips: [
      'List of designated individuals authorized to post publicly',
      'Training completion records for those individuals',
      'Content review checklist or approval workflow records',
    ],
    common_mistakes: 'Allowing any employee to post on the public website or social media without authorization or a review step.',
    nist_reference: 'AC-22',
  },

  'AC-23': {
    plain_english: 'If your system stores PII, you must have controls to detect and prevent unauthorized automated data mining or bulk extraction of that PII.',
    example: 'A rate limiter and anomaly detection rule flags any API call that returns more than 500 user records in a single request, triggering a security alert.',
    evidence_tips: [
      'DLP or UEBA tool configuration targeting bulk-export patterns',
      'API rate-limiting configuration',
      'Alert rules or SIEM queries for large data retrievals',
    ],
    common_mistakes: 'Having no controls on bulk data export, making it trivial for an insider or compromised account to exfiltrate an entire user database.',
    nist_reference: 'AC-23',
  },

  'AC-24': {
    plain_english: 'Access control decisions must be made by designated, competent authorities — not ad-hoc — with documented procedures ensuring consistent enforcement.',
    example: 'An Access Control Board reviews and approves all elevated-privilege requests; no access is granted by a single person without board sign-off.',
    evidence_tips: [
      'Access decision authority matrix identifying named approvers',
      'Ticket records showing approval chain for privileged access requests',
    ],
    common_mistakes: 'Allowing self-approval of access requests, or having no documented process for who authorizes what levels of access.',
    nist_reference: 'AC-24',
  },

  'AC-25': {
    plain_english: 'The system must implement a reference monitor — a tamper-proof, always-invoked mechanism that enforces all access control decisions and can be verified correct.',
    example: 'A cloud platform\'s IAM enforcement engine intercepts every API call, checks it against policies, and cannot be bypassed even by privileged users.',
    evidence_tips: [
      'Architecture documentation describing the reference monitor design',
      'Penetration test results confirming the enforcement mechanism cannot be bypassed',
      'Code review or formal verification evidence for the enforcement component',
    ],
    common_mistakes: 'Allowing certain privileged roles or "break-glass" mechanisms to bypass the access enforcement layer entirely without logging or review.',
    nist_reference: 'AC-25',
  },

  'AC-2(1)': {
    plain_english: 'An automated system must generate audit records whenever accounts are created, modified, enabled, disabled, or removed, and notify designated personnel.',
    example: 'Every account lifecycle event in Azure AD triggers an automated alert to the security team\'s inbox and is logged in the SIEM with actor, action, and timestamp.',
    evidence_tips: [
      'SIEM or cloud audit log configuration showing account event capture',
      'Sample alert emails or tickets generated by account changes',
      'Evidence that designated personnel receive and acknowledge notifications',
    ],
    common_mistakes: 'Relying on manual review of account change logs rather than automated alerting — events can be missed for weeks.',
    nist_reference: 'AC-2(1)',
  },

  'AC-2(2)': {
    plain_english: 'Temporary and emergency accounts must automatically expire and be disabled after a defined period — no manual step should be required to close them.',
    example: 'Contractor accounts are created with a 90-day expiry in the directory; they auto-disable on day 91 and require a formal reauthorization request to re-enable.',
    evidence_tips: [
      'Directory or IAM configuration showing automatic expiry settings for temporary accounts',
      'Audit log showing automated account disablement events',
      'Policy defining maximum lifespan for temporary and emergency accounts',
    ],
    common_mistakes: 'Creating temporary accounts manually with a note to "remember to delete" — these routinely stay active long after the need has passed.',
    nist_reference: 'AC-2(2)',
  },

  // ── AUDIT AND ACCOUNTABILITY (AU) ─────────────────────────────────────────

  'AU-1': {
    plain_english: 'You need a written audit and accountability policy covering purpose, scope, roles, and responsibilities, plus documented procedures for how auditing is implemented.',
    example: 'An Audit & Accountability Policy is published on the intranet, reviewed annually, and paired with an Audit Logging SOP that details which events are captured and how logs are stored.',
    evidence_tips: [
      'Current approved Audit and Accountability Policy document',
      'Procedures document with step-by-step logging and review instructions',
      'Annual review evidence (change log or re-approval signature)',
    ],
    common_mistakes: 'Having audit logging turned on technically but no written policy defining scope, retention, or review responsibilities.',
    nist_reference: 'AU-1',
  },

  'AU-2': {
    plain_english: 'The organization must define exactly which events need to be logged, coordinating with other teams to ensure the audit strategy is complete and consistent.',
    example: 'A documented Auditable Events List includes logins, privilege escalations, data exports, configuration changes, and failed access attempts — reviewed and agreed with the ISSO.',
    evidence_tips: [
      'Auditable events list with rationale for each event type',
      'Evidence of coordination meeting or sign-off with relevant teams',
      'Mapping of auditable events to log sources',
    ],
    common_mistakes: 'Logging everything without a defined list (making review impossible) or logging too little and missing critical security events.',
    nist_reference: 'AU-2',
  },

  'AU-3': {
    plain_english: 'Every audit record must contain enough detail to answer who, what, when, where, and the outcome — without that, logs are useless for investigation.',
    example: 'Each log entry includes: timestamp (UTC), user ID, source IP, action performed, resource affected, and success/failure status.',
    evidence_tips: [
      'Sample log entries demonstrating all required fields are present',
      'Logging configuration showing which fields are captured per event type',
      'SIEM parsing rule confirming field extraction',
    ],
    common_mistakes: 'Capturing only "login failed" without recording which account, from which IP, or at what exact time.',
    nist_reference: 'AU-3',
  },

  'AU-4': {
    plain_english: 'Allocate enough storage for audit logs so they never run out of space and stop recording — plan capacity and configure alerts before the disk fills.',
    example: 'Log storage is sized for 90 days of retention with a CloudWatch alarm that fires when storage exceeds 80%, triggering an automated archive to S3.',
    evidence_tips: [
      'Capacity planning documentation or storage sizing calculation',
      'Storage utilization alert configuration',
      'Evidence of log archival process before storage is exhausted',
    ],
    common_mistakes: 'Setting a fixed log volume without monitoring it — logs silently stop when storage fills and no one notices until an incident.',
    nist_reference: 'AU-4',
  },

  'AU-5': {
    plain_english: 'If the audit logging system fails, the system must alert designated personnel immediately and take predefined actions (e.g., halt operations or overwrite oldest logs).',
    example: 'If the SIEM agent goes offline or log ingestion fails for more than 5 minutes, an automated PagerDuty alert wakes the on-call engineer.',
    evidence_tips: [
      'Alert configuration for audit logging failures',
      'Documented response procedure for audit system failures',
      'Test evidence showing alert fires when logging is disrupted',
    ],
    common_mistakes: 'Not monitoring the health of the logging pipeline itself — a misconfigured agent can silently drop logs for days.',
    nist_reference: 'AU-5',
  },

  'AU-6': {
    plain_english: 'Audit logs must actually be reviewed on a defined schedule to detect suspicious activity — logging without review provides no security value.',
    example: 'The SOC reviews SIEM dashboards daily for high-priority alerts and conducts a weekly full log review, documenting findings and escalating anomalies.',
    evidence_tips: [
      'Log review schedule and assigned responsible personnel',
      'Documented review records or tickets generated from findings',
      'SIEM alert rules and weekly summary reports',
    ],
    common_mistakes: 'Collecting logs but never reviewing them — this is one of the most common audit failures and provides a false sense of security.',
    nist_reference: 'AU-6',
  },

  'AU-7': {
    plain_english: 'The system must support on-demand audit log reduction and report generation — investigators need to filter and analyze logs quickly without altering original records.',
    example: 'A SIEM tool allows analysts to filter logs by user, time range, and event type, exporting reports without modifying the underlying immutable log store.',
    evidence_tips: [
      'SIEM or log management tool configuration demonstrating filtering and reporting',
      'Screenshot of a generated audit report',
      'Documentation confirming original logs are preserved and unmodified',
    ],
    common_mistakes: 'Using tools that modify or delete logs as part of the reduction process, destroying the original audit trail.',
    nist_reference: 'AU-7',
  },

  'AU-8': {
    plain_english: 'All audit record timestamps must come from synchronized system clocks and be mappable to UTC to ensure log correlation across systems.',
    example: 'All servers sync to an NTP server; logs are stored in UTC and the SIEM confirms timestamp consistency during ingestion.',
    evidence_tips: [
      'NTP configuration on all systems generating audit logs',
      'Evidence of time synchronization monitoring',
      'SIEM timestamp normalization configuration',
    ],
    common_mistakes: 'Having servers in different time zones with no NTP sync, making it impossible to correlate events across systems during incident response.',
    nist_reference: 'AU-8',
  },

  'AU-9': {
    plain_english: 'Audit logs and the tools used to manage them must be protected from unauthorized access, modification, or deletion — including by administrators.',
    example: 'Logs are written to an immutable S3 bucket with Object Lock enabled; only the logging service role has write access, and no human has delete permissions.',
    evidence_tips: [
      'Log storage access control configuration (IAM policies, bucket policies)',
      'Evidence of immutable or WORM storage configuration',
      'Alert rules for unauthorized access attempts on log storage',
    ],
    common_mistakes: 'Storing logs on the same system being monitored, where a compromised admin account could delete evidence of the breach.',
    nist_reference: 'AU-9',
  },

  'AU-10': {
    plain_english: 'The system must ensure that users cannot deny having performed an action — cryptographic signatures or similar mechanisms bind actions to identities.',
    example: 'All administrative API actions are signed with the user\'s IAM credentials and logged with a hash that cannot be forged, providing non-repudiation evidence.',
    evidence_tips: [
      'Documentation of digital signature or non-repudiation mechanism',
      'Audit trail showing cryptographically bound user actions',
      'Legal or forensic review confirming non-repudiation capability',
    ],
    common_mistakes: 'Relying solely on username fields in logs — without cryptographic binding, a user can claim their credentials were stolen and deny the action.',
    nist_reference: 'AU-10',
  },

  'AU-11': {
    plain_english: 'Audit records must be retained for a defined period (FedRAMP requires at least 90 days online and 1 year total) to support forensic investigations.',
    example: 'Logs are kept in hot storage for 90 days for live querying and archived to S3 Glacier for an additional 9 months, meeting the 1-year total retention requirement.',
    evidence_tips: [
      'Log retention policy specifying online and archive retention periods',
      'Storage lifecycle configuration enforcing the retention periods',
      'Confirmation that archived logs are retrievable and intact',
    ],
    common_mistakes: 'Deleting logs after 30 days to save storage costs, which prevents post-incident investigation and violates FedRAMP requirements.',
    nist_reference: 'AU-11',
  },

  'AU-12': {
    plain_english: 'Every system component must be capable of generating the audit records defined in AU-2, and designated personnel must be able to select which events each component logs.',
    example: 'All EC2 instances, Lambda functions, and RDS instances have CloudTrail and CloudWatch logging enabled; the ISSO can adjust the log verbosity per component via the logging config.',
    evidence_tips: [
      'Inventory of system components with logging capability confirmed for each',
      'Logging configuration per component showing enabled event types',
      'Evidence that the ISSO or security team can modify logging settings',
    ],
    common_mistakes: 'Enabling logging on servers but forgetting network devices, cloud functions, or container workloads — leaving blind spots in the audit trail.',
    nist_reference: 'AU-12',
  },

  // ── SECURITY ASSESSMENT AND AUTHORIZATION (CA) ───────────────────────────

  'CA-1': {
    plain_english: 'You need a written security assessment and authorization policy covering purpose, scope, roles, and responsibilities, plus procedures for how assessments are planned and executed.',
    example: 'A CA Policy document is published, reviewed annually, and paired with an Assessment SOP detailing assessment frequency, assessor requirements, and report formats.',
    evidence_tips: [
      'Current approved CA Policy document',
      'Assessment procedures with defined frequency and scope',
      'Annual review evidence',
    ],
    common_mistakes: 'Treating the ATO package as the only documentation — without a standing policy, there is no framework for ongoing assessments.',
    nist_reference: 'CA-1',
  },

  'CA-2': {
    plain_english: 'The organization must conduct formal security assessments of the system at a defined frequency, using sufficiently independent assessors, and produce reports documenting findings.',
    example: 'An independent 3PAO conducts an annual security assessment of all FedRAMP controls, produces a Security Assessment Report (SAR), and the ISSO reviews findings within 30 days.',
    evidence_tips: [
      'Security Assessment Report (SAR) from most recent assessment',
      'Assessor independence documentation (3PAO accreditation or org-chart separation)',
      'Assessment schedule showing defined frequency',
    ],
    common_mistakes: 'Having the system owner assess their own controls — FedRAMP requires sufficient independence to ensure objectivity.',
    nist_reference: 'CA-2',
  },

  'CA-3': {
    plain_english: 'Connections to external systems must be formally authorized via Interconnection Security Agreements (ISAs) that document the interface, data exchanged, and security requirements.',
    example: 'An ISA is signed with each connected agency system describing the data types, encryption requirements, and point-of-contact before the connection is established.',
    evidence_tips: [
      'Signed ISAs or MOUs for each external system interconnection',
      'Network diagram showing all external connections',
      'Review records confirming ISAs are updated when connections change',
    ],
    common_mistakes: 'Connecting to external systems without a formal agreement, or using an outdated ISA that no longer reflects the current data flows.',
    nist_reference: 'CA-3',
  },

  'CA-5': {
    plain_english: 'The organization must maintain a Plan of Action and Milestones (POA&M) documenting known weaknesses, who is responsible for fixing them, and target remediation dates.',
    example: 'A POA&M spreadsheet in the ATO package lists each open finding from the SAR, assigns an owner, and sets a remediation milestone date that is reviewed monthly.',
    evidence_tips: [
      'Current POA&M with open findings, owners, and target dates',
      'Monthly POA&M review meeting minutes or update logs',
      'Evidence of closed items with completion dates',
    ],
    common_mistakes: 'Creating a POA&M only for ATO submission and never updating it — the POA&M must reflect the current state of open findings at all times.',
    nist_reference: 'CA-5',
  },

  'CA-6': {
    plain_english: 'An Authorizing Official (AO) must formally authorize the system to operate, and that authorization must be reviewed at least annually or when significant changes occur.',
    example: 'The AO signs the Authorization to Operate (ATO) letter valid for 3 years; the ISSO notifies the AO of significant changes that may require re-authorization.',
    evidence_tips: [
      'Signed ATO letter or equivalent authorization decision document',
      'Evidence of annual ATO status review',
      'Significant change notification records',
    ],
    common_mistakes: 'Letting an ATO expire without renewal, or making significant system changes without notifying the AO that re-authorization may be needed.',
    nist_reference: 'CA-6',
  },

  'CA-7': {
    plain_english: 'The organization must implement a continuous monitoring program that tracks the security posture of the system on an ongoing basis, not just during annual assessments.',
    example: 'A ConMon plan defines monthly vulnerability scans, quarterly control reviews, and real-time security alerting, with monthly reports submitted to the AO.',
    evidence_tips: [
      'Continuous monitoring strategy or plan document',
      'Monthly ConMon reports submitted to the AO',
      'Automated scanning results and remediation tracking records',
    ],
    common_mistakes: 'Treating continuous monitoring as just annual vulnerability scanning — FedRAMP ConMon requires ongoing activities with monthly deliverables.',
    nist_reference: 'CA-7',
  },

  'CA-8': {
    plain_english: 'The organization must conduct penetration testing of the system at a defined frequency to find vulnerabilities that automated scanning may miss.',
    example: 'An annual penetration test by an independent 3PAO targets all external-facing system components; findings are fed into the POA&M within 30 days.',
    evidence_tips: [
      'Penetration test report from most recent engagement',
      'Evidence of tester independence and qualification',
      'POA&M entries corresponding to pentest findings',
    ],
    common_mistakes: 'Running only automated vulnerability scans and calling it a penetration test — manual exploitation attempts are required to satisfy this control.',
    nist_reference: 'CA-8',
  },

  'CA-9': {
    plain_english: 'Internal connections between system components must be formally authorized before being established, with the interface and data flows documented.',
    example: 'Before connecting a new microservice to the data tier, the security team reviews and approves the internal connection request, documenting ports, protocols, and data types.',
    evidence_tips: [
      'Internal connection authorization records or change tickets',
      'System architecture diagram showing internal component connections',
      'Annual review evidence confirming documented connections remain current',
    ],
    common_mistakes: 'Documenting only external interconnections in ISAs but never formally authorizing internal service-to-service connections.',
    nist_reference: 'CA-9',
  },

  'CA-2(1)': {
    plain_english: 'Assessors conducting security control assessments must be sufficiently independent from the system owner to provide an objective evaluation.',
    example: 'A FedRAMP-accredited 3PAO performs the annual assessment; no member of the system development or operations team participates as an assessor.',
    evidence_tips: [
      '3PAO accreditation certificate or assessor independence declaration',
      'Organizational chart confirming assessor separation from system team',
    ],
    common_mistakes: 'Using internal staff to assess controls they own or operate — this creates a conflict of interest that invalidates assessment objectivity.',
    nist_reference: 'CA-2(1)',
  },

  // ── CONFIGURATION MANAGEMENT (CM) ────────────────────────────────────────

  'CM-1': {
    plain_english: 'You need a written configuration management policy covering purpose, scope, roles, and responsibilities, plus procedures for how configuration management is performed.',
    example: 'A CM Policy is published covering baseline management, change control, and inventory requirements, paired with a CM SOP detailing step-by-step procedures.',
    evidence_tips: [
      'Current approved CM Policy document',
      'CM procedures document',
      'Annual review evidence',
    ],
    common_mistakes: 'Relying on informal change practices with no documented CM policy — leading to undocumented configuration drift over time.',
    nist_reference: 'CM-1',
  },

  'CM-2': {
    plain_english: 'The organization must maintain a documented baseline configuration for the system that reflects its current approved state and is updated whenever authorized changes occur.',
    example: 'Infrastructure-as-code in a Git repo serves as the baseline; all configuration changes go through pull requests and the baseline is tagged at each approved release.',
    evidence_tips: [
      'Baseline configuration document or IaC repository with version history',
      'Evidence of baseline updates following approved changes',
      'Comparison showing current system state matches the documented baseline',
    ],
    common_mistakes: 'Creating a baseline configuration document once and never updating it — within months it no longer reflects the real system.',
    nist_reference: 'CM-2',
  },

  'CM-3': {
    plain_english: 'All changes to the system must go through a formal change control process including security impact analysis, testing, and approval before implementation.',
    example: 'All production changes require a Change Request ticket reviewed by the Change Advisory Board; a security impact analysis is completed before approval.',
    evidence_tips: [
      'Change management policy and procedures',
      'CAB meeting minutes or ticket approval records',
      'Security impact analysis templates and completed examples',
    ],
    common_mistakes: 'Approving changes without a security impact analysis, or making emergency changes that bypass the change control process without after-the-fact review.',
    nist_reference: 'CM-3',
  },

  'CM-4': {
    plain_english: 'Before implementing any system change, the organization must analyze the potential security and privacy impact to ensure the change does not introduce new vulnerabilities.',
    example: 'A security engineer completes a Security Impact Analysis (SIA) checklist for every proposed change before it is approved for production deployment.',
    evidence_tips: [
      'Completed SIA forms linked to change tickets',
      'Evidence that security team reviews SIAs before change approval',
    ],
    common_mistakes: 'Treating security impact analysis as a rubber stamp rather than a genuine evaluation of risks introduced by a change.',
    nist_reference: 'CM-4',
  },

  'CM-5': {
    plain_english: 'Physical and logical access to make changes to the system must be restricted to authorized personnel only, enforced technically where possible.',
    example: 'Only the release engineering team has production deployment permissions; all others are read-only, and deployment access requires manager approval in the ticketing system.',
    evidence_tips: [
      'RBAC configuration showing restricted change access',
      'Access approval records for change-authorized roles',
      'Audit log evidence of who has made system changes',
    ],
    common_mistakes: 'Giving all developers production write access for convenience — this makes it impossible to enforce change control or detect unauthorized changes.',
    nist_reference: 'CM-5',
  },

  'CM-6': {
    plain_english: 'The organization must establish and document configuration settings for all technology components using the most restrictive settings consistent with operational needs (e.g., DISA STIGs or CIS Benchmarks).',
    example: 'All servers are configured per the applicable CIS Benchmark level 2 profile; deviations are documented with a risk acceptance from the ISSO.',
    evidence_tips: [
      'Configuration baseline document referencing STIG or CIS Benchmark',
      'Automated compliance scan results against the benchmark',
      'Documented and approved deviations from the baseline',
    ],
    common_mistakes: 'Using default vendor configurations instead of hardened baselines — default settings are well-known and commonly exploited.',
    nist_reference: 'CM-6',
  },

  'CM-7': {
    plain_english: 'The system should be configured to provide only the functions, ports, protocols, and services actually needed — everything else must be disabled or blocked.',
    example: 'Firewall rules allow only ports 443 and 22 inbound; all unused services (FTP, Telnet, HTTP) are disabled on all servers and validated by quarterly port scans.',
    evidence_tips: [
      'Port and protocol justification matrix listing only approved services',
      'Firewall or security group configuration confirming unused ports are blocked',
      'Periodic port scan results confirming no unexpected services are running',
    ],
    common_mistakes: 'Leaving default services enabled because they "might be needed someday" — every open port and running service is a potential attack surface.',
    nist_reference: 'CM-7',
  },

  'CM-8': {
    plain_english: 'The organization must maintain an accurate, up-to-date inventory of all system components and verify the inventory at a defined frequency.',
    example: 'An automated asset discovery tool scans the environment weekly and reconciles results against the component inventory; discrepancies trigger a security alert.',
    evidence_tips: [
      'System component inventory (CMDB or equivalent) with last-updated date',
      'Automated inventory scan configuration and results',
      'Reconciliation records showing inventory matches discovered assets',
    ],
    common_mistakes: 'Maintaining a spreadsheet inventory that is manually updated — it falls out of sync with reality within days of a new deployment.',
    nist_reference: 'CM-8',
  },

  'CM-9': {
    plain_english: 'The organization must have a formal Configuration Management Plan that describes how CM is implemented, who is responsible, and how it integrates with the system development lifecycle.',
    example: 'A CM Plan document describes the CM roles, the baseline management process, the change control board membership, and how CM gates are enforced in the CI/CD pipeline.',
    evidence_tips: [
      'Configuration Management Plan document with current version and approval date',
      'Evidence of CM plan review at least annually',
      'Integration evidence showing CM controls are applied in the SDLC',
    ],
    common_mistakes: 'Treating CM as only a production concern — security configuration requirements should be enforced from development through deployment.',
    nist_reference: 'CM-9',
  },

  'CM-10': {
    plain_english: 'The organization must track software licenses to ensure compliance and prevent unauthorized copying or use of licensed software.',
    example: 'A software asset management tool tracks all licenses, alerts when usage nears the license cap, and blocks installation of unlicensed software on managed endpoints.',
    evidence_tips: [
      'Software license inventory and tracking records',
      'SAM tool configuration or license compliance reports',
      'Policy prohibiting installation of unauthorized software',
    ],
    common_mistakes: 'Installing software without tracking license counts, creating compliance liability and introducing unapproved software into the environment.',
    nist_reference: 'CM-10',
  },

  'CM-11': {
    plain_english: 'The organization must define who can install software, enforce that policy technically, and monitor for unauthorized software installations.',
    example: 'Standard users cannot install software on managed endpoints; admin rights are required and logged. Unauthorized installs trigger an alert and automated quarantine.',
    evidence_tips: [
      'Policy governing user software installation rights',
      'Endpoint management configuration restricting installation to privileged accounts',
      'Monitoring alerts for unauthorized software installation attempts',
    ],
    common_mistakes: 'Giving all users local admin rights, making it impossible to prevent or detect unauthorized software installation.',
    nist_reference: 'CM-11',
  },

  // ── CONTINGENCY PLANNING (CP) ─────────────────────────────────────────────

  'CP-1': {
    plain_english: 'You need a written contingency planning policy covering purpose, scope, roles, and responsibilities, plus procedures for how contingency plans are developed and maintained.',
    example: 'A CP Policy document is approved by the ISSO, reviewed annually, and paired with a Contingency Planning SOP describing plan development, testing, and update procedures.',
    evidence_tips: [
      'Current approved CP Policy document',
      'CP procedures document',
      'Annual review evidence',
    ],
    common_mistakes: 'Having a contingency plan but no governing policy — without defined roles and responsibilities, plan execution during a real incident becomes chaotic.',
    nist_reference: 'CP-1',
  },

  'CP-2': {
    plain_english: 'The organization must develop, maintain, and communicate a formal Contingency Plan that covers how the system will be recovered and at what priority.',
    example: 'A Contingency Plan document defines the system\'s RTO/RPO, recovery procedures, roles, escalation contacts, and is reviewed annually and after each test or incident.',
    evidence_tips: [
      'Contingency Plan document with defined RTO/RPO',
      'Evidence of annual review and updates',
      'Distribution records showing relevant personnel received the plan',
    ],
    common_mistakes: 'Setting unrealistically aggressive RTOs that cannot be achieved with existing backup and recovery capabilities.',
    nist_reference: 'CP-2',
  },

  'CP-3': {
    plain_english: 'Personnel with contingency roles must be trained before assuming those roles, when the plan changes, and at least annually.',
    example: 'Annual tabletop exercises are held for all contingency team members; new personnel complete contingency training within 30 days of being assigned a role.',
    evidence_tips: [
      'Training completion records for contingency personnel',
      'Tabletop exercise agenda and attendance records',
      'Training content addressing current plan procedures',
    ],
    common_mistakes: 'Training personnel once at hire and never again — annual refreshers ensure everyone knows the current plan and their role.',
    nist_reference: 'CP-3',
  },

  'CP-4': {
    plain_english: 'The contingency plan must be tested at a defined frequency to verify it actually works and the organization can execute it under realistic conditions.',
    example: 'An annual full failover test is conducted; a quarterly tabletop exercise tests decision-making. Results are documented and fed back into plan updates.',
    evidence_tips: [
      'Contingency plan test reports with date, scope, and results',
      'After-action review documenting lessons learned',
      'Evidence that test findings resulted in plan updates',
    ],
    common_mistakes: 'Only conducting tabletop exercises and never testing actual recovery procedures — a plan that has never been practiced will fail during a real incident.',
    nist_reference: 'CP-4',
  },

  'CP-6': {
    plain_english: 'The organization must establish an alternate storage site geographically separated from the primary site, with equivalent security controls for backup data.',
    example: 'Production data is replicated to an AWS region in a different geographic area; the alternate site is governed by the same security controls as the primary.',
    evidence_tips: [
      'Alternate storage site agreement or cloud region configuration',
      'Evidence of geographic separation (distance or disaster zone analysis)',
      'Security control equivalency documentation for the alternate site',
    ],
    common_mistakes: 'Storing backups in the same building or data center as production — a single site disaster would destroy both the primary and the backup.',
    nist_reference: 'CP-6',
  },

  'CP-7': {
    plain_english: 'The organization must establish an alternate processing site that can take over operations if the primary site fails, with equivalent security controls.',
    example: 'A hot standby environment in a second cloud region can assume production workloads within the defined RTO; it is tested annually via a failover drill.',
    evidence_tips: [
      'Alternate processing site agreement or cloud failover configuration',
      'Failover test results demonstrating the site can handle production load',
      'Security equivalency documentation for the alternate processing site',
    ],
    common_mistakes: 'Designating an alternate site that has never been tested for actual capacity or connectivity — it may be unable to handle production workloads under stress.',
    nist_reference: 'CP-7',
  },

  'CP-8': {
    plain_english: 'The organization must have alternate telecommunications services available if primary communications fail, to support mission-critical operations.',
    example: 'A secondary ISP contract provides failover internet connectivity; a satellite link is available as a last-resort alternative for out-of-band management.',
    evidence_tips: [
      'Alternate telecommunications service agreement',
      'Evidence of periodic testing of the alternate communications path',
      'Network diagram showing primary and alternate connectivity',
    ],
    common_mistakes: 'Having only a single internet provider with no failover path — a single provider outage can take down both the system and the ability to respond to the incident.',
    nist_reference: 'CP-8',
  },

  'CP-9': {
    plain_english: 'The organization must back up user data, system data, and documentation at defined frequencies and protect backups from unauthorized access.',
    example: 'Daily incremental and weekly full backups run automatically; backups are encrypted at rest, stored off-site, and verified for integrity monthly via restore tests.',
    evidence_tips: [
      'Backup schedule configuration showing frequency and scope',
      'Backup job completion logs',
      'Restore test records confirming backups are usable',
      'Evidence of backup encryption and access controls',
    ],
    common_mistakes: 'Taking backups but never testing restores — a backup that cannot be successfully restored provides no protection.',
    nist_reference: 'CP-9',
  },

  'CP-10': {
    plain_english: 'The system must be able to recover and reconstitute to a known good state after a disruption, compromise, or failure within the defined RTO.',
    example: 'Recovery runbooks describe step-by-step procedures to restore each system component from backup; a full recovery drill is conducted annually and timed against the RTO.',
    evidence_tips: [
      'Recovery procedures or runbooks for each critical component',
      'Recovery drill results showing achieved recovery time vs. RTO',
      'Evidence of "known good state" baselines used for reconstitution',
    ],
    common_mistakes: 'Defining an RTO without validating whether it is achievable — many organizations discover RTO violations only during an actual incident.',
    nist_reference: 'CP-10',
  },

  'CP-11': {
    plain_english: 'If primary communications protocols fail, the system must be able to switch to alternate protocols that maintain equivalent security capabilities.',
    example: 'If TLS-based API calls fail, the system falls back to a message queue using the same encryption standards; the fallback protocol is tested quarterly.',
    evidence_tips: [
      'Documentation of alternate protocol capabilities and their security equivalency',
      'Test evidence confirming fallback protocols activate correctly',
    ],
    common_mistakes: 'Designing alternate communication paths that use weaker encryption or authentication than the primary path, creating a security regression under failure conditions.',
    nist_reference: 'CP-11',
  },

  'CP-12': {
    plain_english: 'The system must support a "safe mode" during recovery that activates a subset of controls sufficient to protect the system while full functionality is restored.',
    example: 'During recovery, the system enters maintenance mode: external access is blocked, only essential admin functions are available, and all actions are logged for post-incident review.',
    evidence_tips: [
      'Safe mode or maintenance mode design documentation',
      'Evidence of safe mode controls being tested during recovery drills',
    ],
    common_mistakes: 'Bringing systems back online in an unprotected state during recovery to "save time" — this can expose the system to attack during a vulnerable window.',
    nist_reference: 'CP-12',
  },

  'CP-13': {
    plain_english: 'When a required security control cannot be implemented, the organization must employ compensating controls that provide equivalent protection.',
    example: 'Because a legacy system cannot support MFA, compensating controls include network isolation, enhanced monitoring, and privileged-access workstations for all access.',
    evidence_tips: [
      'Compensating control documentation with rationale for each exception',
      'Risk acceptance signed by the AO for each compensating control',
      'Evidence that compensating controls are implemented and monitored',
    ],
    common_mistakes: 'Accepting compensating controls without documenting the residual risk or obtaining formal AO approval — leaving the risk unacknowledged and unmanaged.',
    nist_reference: 'CP-13',
  },

  'CP-2(1)': {
    plain_english: 'The contingency plan must be coordinated with other related plans (business continuity, disaster recovery, COOP) to ensure they are consistent and mutually supporting.',
    example: 'The system contingency plan is reviewed alongside the agency\'s COOP plan annually; a cross-team meeting confirms alignment of recovery priorities and contact lists.',
    evidence_tips: [
      'Cross-reference documentation showing alignment between CP and related plans',
      'Meeting minutes from coordination session with BCP/DR/COOP plan owners',
    ],
    common_mistakes: 'Developing the system contingency plan in isolation — conflicting recovery priorities between plans can cause confusion and delays during an actual incident.',
    nist_reference: 'CP-2(1)',
  },

  // ── IDENTIFICATION AND AUTHENTICATION (IA) ────────────────────────────────

  'IA-1': {
    plain_english: 'You need a written identification and authentication policy covering purpose, scope, roles, and responsibilities, plus procedures for how I&A is implemented across the system.',
    example: 'An I&A Policy document is published, reviewed annually, and paired with an I&A SOP describing password requirements, MFA enrollment, and account provisioning procedures.',
    evidence_tips: [
      'Current approved I&A Policy document',
      'I&A procedures document',
      'Annual review evidence',
    ],
    common_mistakes: 'Having a password policy buried in an employee handbook but no formal I&A policy that addresses system-level authentication requirements.',
    nist_reference: 'IA-1',
  },

  'IA-2': {
    plain_english: 'Every user and process must be uniquely identified and authenticated before being granted access — shared or anonymous accounts are not permitted.',
    example: 'Each employee has a unique username and must complete MFA before accessing any organizational system; service accounts are named after the application, not a person.',
    evidence_tips: [
      'Identity provider configuration showing unique account per user',
      'MFA enrollment records or configuration screenshots',
      'Policy prohibiting shared accounts',
    ],
    common_mistakes: 'Using shared "admin" or "service" accounts that multiple people log into — this makes attribution of actions impossible.',
    nist_reference: 'IA-2',
  },

  'IA-3': {
    plain_english: 'Devices connecting to the network must be uniquely identified and authenticated before being allowed to communicate — not just users.',
    example: 'Servers use certificate-based authentication via 802.1X before joining the network; unrecognized devices are placed in an isolation VLAN pending review.',
    evidence_tips: [
      'Device certificate or 802.1X configuration documentation',
      'NAC (Network Access Control) configuration showing device authentication enforcement',
      'Inventory of authenticated devices',
    ],
    common_mistakes: 'Authenticating only users but allowing any device onto the network, creating risk from rogue or compromised endpoints.',
    nist_reference: 'IA-3',
  },

  'IA-4': {
    plain_english: 'The organization must manage identifiers throughout their lifecycle — assigning unique IDs, preventing reuse, and disabling identifiers after inactivity.',
    example: 'User IDs follow a defined naming convention, are never reused after an employee leaves, and are automatically disabled after 90 days of inactivity.',
    evidence_tips: [
      'Identity management policy covering identifier uniqueness and reuse prevention',
      'Directory configuration showing inactivity-based disablement settings',
      'Records showing departed employee IDs are not reassigned',
    ],
    common_mistakes: 'Recycling old usernames for new employees — this can grant the new user access to the former employee\'s residual permissions or data.',
    nist_reference: 'IA-4',
  },

  'IA-5': {
    plain_english: 'Authenticators (passwords, tokens, keys, certificates) must be properly managed — enforcing complexity, protecting them from disclosure, and revoking them when compromised.',
    example: 'Password policy enforces minimum 12 characters with complexity, 90-day expiry, and prohibition of the last 12 passwords; compromised credentials trigger immediate reset.',
    evidence_tips: [
      'Password policy configuration in the identity provider',
      'MFA token management and provisioning records',
      'Process documentation for revoking compromised authenticators',
    ],
    common_mistakes: 'Setting weak password policies or never requiring rotation of long-lived API keys and service account credentials.',
    nist_reference: 'IA-5',
  },

  'IA-6': {
    plain_english: 'During authentication, the system must obscure feedback (e.g., mask passwords) so bystanders or observers cannot see credentials being entered.',
    example: 'The login form masks password characters as dots; error messages say "invalid username or password" rather than revealing which field was wrong.',
    evidence_tips: [
      'Screenshot of login form showing masked password field',
      'Code review confirming authentication error messages do not reveal valid usernames',
    ],
    common_mistakes: 'Displaying verbose error messages like "username not found" that help attackers enumerate valid account names.',
    nist_reference: 'IA-6',
  },

  'IA-7': {
    plain_english: 'Cryptographic authentication mechanisms must meet applicable federal standards (FIPS 140-2/3 validated modules) for authenticating to cryptographic components.',
    example: 'The system uses FIPS 140-2 validated cryptographic modules for all authentication operations; a certificate of validation is on file for each module used.',
    evidence_tips: [
      'FIPS 140-2/3 validation certificates for cryptographic modules in use',
      'System documentation mapping authentication operations to validated modules',
    ],
    common_mistakes: 'Using cryptographic libraries that are not FIPS-validated, which may be functionally equivalent but do not satisfy federal compliance requirements.',
    nist_reference: 'IA-7',
  },

  'IA-8': {
    plain_english: 'Non-organizational users (external partners, public users) must also be uniquely identified and authenticated before accessing the system.',
    example: 'External contractors authenticate via a federated identity provider with MFA; public users accessing a portal create unique accounts with email verification.',
    evidence_tips: [
      'Identity federation configuration for external users',
      'External user authentication policy and enrollment procedures',
      'Audit log samples showing unique authentication events for non-org users',
    ],
    common_mistakes: 'Applying strong I&A requirements to internal users but allowing external partners to authenticate with only a shared password.',
    nist_reference: 'IA-8',
  },

  'IA-9': {
    plain_english: 'System services and applications that communicate with each other must be identified and authenticated, not just human users.',
    example: 'Microservices authenticate to each other using mTLS certificates; API calls between services include a service identity token validated by the API gateway.',
    evidence_tips: [
      'Service-to-service authentication configuration (mTLS, service accounts, API keys)',
      'Architecture documentation showing how service identities are managed',
    ],
    common_mistakes: 'Securing user-facing endpoints with strong authentication but allowing internal service calls to flow unauthenticated over a "trusted" internal network.',
    nist_reference: 'IA-9',
  },

  'IA-10': {
    plain_english: 'Under specific conditions defined by the organization (e.g., high-risk actions), users must provide supplemental authentication beyond their initial login.',
    example: 'Any attempt to delete production data or modify IAM policies triggers a step-up authentication prompt requiring re-entry of MFA credentials.',
    evidence_tips: [
      'Documented list of conditions requiring supplemental authentication',
      'Technical configuration of step-up authentication for defined actions',
      'Test evidence confirming step-up prompts fire for the defined conditions',
    ],
    common_mistakes: 'Treating a single login session as perpetually authorized for all actions, even highly sensitive ones that warrant additional verification.',
    nist_reference: 'IA-10',
  },

  'IA-11': {
    plain_english: 'Users and devices must re-authenticate when defined circumstances arise, such as privilege escalation, role changes, or after a defined inactivity period.',
    example: 'Users must re-authenticate with MFA when switching from a standard role to an elevated admin role, even within an active session.',
    evidence_tips: [
      'Re-authentication trigger configuration in the identity provider or application',
      'Policy defining the conditions that require re-authentication',
      'Test evidence showing re-authentication prompts for defined conditions',
    ],
    common_mistakes: 'Allowing privilege escalation within an existing session without re-authentication, making it easy for an attacker with a hijacked session to elevate privileges.',
    nist_reference: 'IA-11',
  },

  'IA-12': {
    plain_english: 'Before issuing credentials, the organization must verify the identity of individuals through a formal identity proofing process that meets the required assurance level.',
    example: 'New employees complete in-person identity proofing with government-issued ID verification before credentials are provisioned; remote hires use a NIST IAL2-compliant video verification service.',
    evidence_tips: [
      'Identity proofing policy and procedures',
      'Records of identity proofing completion for each account holder',
      'Evidence that the proofing method meets the required Identity Assurance Level (IAL)',
    ],
    common_mistakes: 'Issuing credentials based solely on an email address or manager approval without verifying the physical identity of the person receiving the account.',
    nist_reference: 'IA-12',
  },

  // ── INCIDENT RESPONSE (IR) ────────────────────────────────────────────────

  'IR-1': {
    plain_english: 'You need a written incident response policy covering purpose, scope, roles, and responsibilities, plus procedures for how incidents are detected, reported, and handled.',
    example: 'An IR Policy document is approved by leadership, reviewed annually, and paired with an IR SOP detailing the detection-to-closure lifecycle for security incidents.',
    evidence_tips: [
      'Current approved IR Policy document',
      'IR procedures document with defined incident categories and escalation paths',
      'Annual review evidence',
    ],
    common_mistakes: 'Having informal "we\'ll figure it out when it happens" response practices with no documented policy — leading to chaotic, inconsistent responses to incidents.',
    nist_reference: 'IR-1',
  },

  'IR-2': {
    plain_english: 'Personnel with incident response roles must receive IR training before assuming those roles, when the plan changes, and at least annually.',
    example: 'All members of the incident response team complete annual IR training including simulated phishing exercises, tabletop scenarios, and an IR procedures review.',
    evidence_tips: [
      'Training completion records for IR team members',
      'Training content covering current IR procedures and roles',
      'Annual training schedule with dates and attendance',
    ],
    common_mistakes: 'Only providing IR training at employee onboarding — responders forget procedures and miss plan updates without regular refreshers.',
    nist_reference: 'IR-2',
  },

  'IR-3': {
    plain_english: 'The organization must test its incident response capability at a defined frequency to measure effectiveness and identify gaps before a real incident occurs.',
    example: 'A tabletop exercise is conducted quarterly simulating different incident types; an annual red team exercise tests detection and response capabilities end-to-end.',
    evidence_tips: [
      'IR test reports with date, scenario, participants, and results',
      'After-action review documenting identified gaps',
      'Evidence that test findings led to IR plan or procedure updates',
    ],
    common_mistakes: 'Never testing the IR plan — organizations discover plan deficiencies only during real incidents when the cost of failure is highest.',
    nist_reference: 'IR-3',
  },

  'IR-4': {
    plain_english: 'The organization must have a formal incident handling capability covering all phases: preparation, detection, analysis, containment, eradication, and recovery.',
    example: 'The IR plan defines step-by-step procedures for each incident phase, with named roles for each step; all incidents are managed through a ticketing system with phase tracking.',
    evidence_tips: [
      'Incident Response Plan covering all six handling phases',
      'Incident tickets showing phase progression for past incidents',
      'Evidence of coordination between IR and contingency planning teams',
    ],
    common_mistakes: 'Focusing only on detection and containment while neglecting eradication and post-incident recovery — incomplete handling allows threats to persist or recur.',
    nist_reference: 'IR-4',
  },

  'IR-5': {
    plain_english: 'The organization must track and document every security incident through its entire handling lifecycle, maintaining records for future analysis and reporting.',
    example: 'All incidents are logged in a ticketing system with severity, timeline, affected systems, actions taken, and resolution details; records are retained for 3 years.',
    evidence_tips: [
      'Incident tracking system or log with complete incident records',
      'Retention policy for incident records',
      'Sample incident records showing complete lifecycle documentation',
    ],
    common_mistakes: 'Handling incidents informally via email or Slack without creating a formal ticket — this makes trend analysis, reporting, and lessons-learned impossible.',
    nist_reference: 'IR-5',
  },

  'IR-6': {
    plain_english: 'All personnel must be required to report suspected incidents to the IR capability within a defined timeframe, and the organization must report to US-CERT/CISA within required windows.',
    example: 'Employees must report suspected incidents to the security team within 1 hour of discovery; the ISSO reports confirmed incidents to US-CERT within 1 hour of confirmation per FedRAMP requirements.',
    evidence_tips: [
      'Incident reporting policy with defined timeframes',
      'Awareness training records showing employees know how to report',
      'US-CERT/CISA reporting records for past incidents',
    ],
    common_mistakes: 'Only requiring security staff to report incidents — all users are potential first detectors and must know their reporting obligation.',
    nist_reference: 'IR-6',
  },

  'IR-7': {
    plain_english: 'The organization must provide an incident response support resource — a help desk, hotline, or team — that users can contact to get guidance on handling and reporting suspected incidents.',
    example: 'A security hotline and a dedicated email alias are published in the employee handbook; the SOC team staffs the hotline 24/7 and responds to emails within 2 hours.',
    evidence_tips: [
      'IR support contact information published in employee-facing resources',
      'SLA or response-time commitment for the IR support function',
      'Records showing the IR support channel is operational and monitored',
    ],
    common_mistakes: 'Making the IR reporting process so complicated that employees give up and don\'t report suspicious activity at all.',
    nist_reference: 'IR-7',
  },

  'IR-8': {
    plain_english: 'The organization must develop and maintain a formal Incident Response Plan that defines the IR capability structure, roles, responsibilities, and procedures, reviewed and updated at a defined frequency.',
    example: 'An IRP document is reviewed annually, updated after each significant incident or organizational change, and version-controlled with distribution tracked to all IR team members.',
    evidence_tips: [
      'Incident Response Plan with version history and approval dates',
      'Annual review records or post-incident update records',
      'Distribution list showing IR team members received the current version',
    ],
    common_mistakes: 'Writing an IRP once for the ATO package and never updating it — after system changes or staff turnover, the plan becomes dangerously out of date.',
    nist_reference: 'IR-8',
  },

  'IR-9': {
    plain_english: 'When an information spill occurs (data sent to unauthorized systems), the organization must follow a defined process to identify, contain, and eradicate the spilled information.',
    example: 'A data spill procedure defines steps to identify what was spilled, notify affected parties, quarantine recipient systems, eradicate the data, and document the incident.',
    evidence_tips: [
      'Data spill response procedures document',
      'Records of any past data spill incidents handled per the procedure',
      'Training evidence showing personnel know how to identify and report spills',
    ],
    common_mistakes: 'Treating a data spill as a generic security incident rather than following a specific containment and eradication process designed for unauthorized data disclosure.',
    nist_reference: 'IR-9',
  },

  'IR-10': {
    plain_english: 'The organization must maintain an integrated team combining forensic expertise, incident response capability, and legal counsel to respond to incidents effectively.',
    example: 'The IR team includes a digital forensics specialist, security analysts, legal counsel on retainer, and a communications officer; all are listed in the IRP with contact details.',
    evidence_tips: [
      'IR team roster showing forensic, legal, and response roles',
      'Retainer agreement or on-call arrangement with legal counsel',
      'Evidence of integrated team participation in tabletop exercises',
    ],
    common_mistakes: 'Building an IR team of only technical staff and looping in legal only after an incident escalates — legal involvement from the start preserves evidence admissibility.',
    nist_reference: 'IR-10',
  },

  // ── MAINTENANCE (MA) ─────────────────────────────────────────────────────

  'MA-1': {
    plain_english: 'You need a written system maintenance policy covering purpose, scope, roles, and responsibilities, plus procedures for how maintenance is planned, performed, and documented.',
    example: 'A Maintenance Policy is published and reviewed annually, paired with a Maintenance SOP describing scheduling, authorization, and documentation requirements for all maintenance activities.',
    evidence_tips: [
      'Current approved MA Policy document',
      'Maintenance procedures document',
      'Annual review evidence',
    ],
    common_mistakes: 'Performing ad-hoc maintenance without a policy, leading to undocumented changes that cannot be traced when system issues arise later.',
    nist_reference: 'MA-1',
  },

  'MA-2': {
    plain_english: 'Maintenance activities on system components must be scheduled, performed per manufacturer specifications, documented, and reviewed to ensure they are conducted properly.',
    example: 'All hardware maintenance is scheduled via a change ticket, performed by authorized technicians, and documented with the technician name, date, actions taken, and outcome.',
    evidence_tips: [
      'Maintenance schedule and records for system components',
      'Change tickets linked to maintenance activities',
      'Maintenance completion documentation reviewed by the ISSO',
    ],
    common_mistakes: 'Allowing vendor technicians to perform maintenance without logging what was done — undocumented maintenance creates unexplained changes in system behavior.',
    nist_reference: 'MA-2',
  },

  'MA-3': {
    plain_english: 'Maintenance tools must be approved, controlled, and inspected to ensure they have not been tampered with or used to introduce malicious software.',
    example: 'Approved maintenance tools are listed in a policy; tools brought on-site by vendors are inspected before use; portable diagnostic tools are scanned for malware before connecting.',
    evidence_tips: [
      'Approved maintenance tool list',
      'Inspection records for maintenance tools',
      'Malware scan results for portable tools before use',
    ],
    common_mistakes: 'Allowing technicians to connect personal laptops or USB drives to systems without inspection — these are a common vector for malware introduction.',
    nist_reference: 'MA-3',
  },

  'MA-4': {
    plain_english: 'Remote maintenance sessions must be explicitly authorized, monitored, use MFA, and be terminated immediately upon completion.',
    example: 'Remote vendor support sessions are initiated by the ISSO, require MFA via the corporate VPN, are recorded in the session management system, and terminated by the ISSO when complete.',
    evidence_tips: [
      'Remote maintenance authorization records for each session',
      'MFA configuration for remote maintenance access',
      'Session recording or audit log evidence',
      'Session termination records',
    ],
    common_mistakes: 'Leaving a remote maintenance channel (e.g., vendor VPN or remote desktop tool) permanently open rather than activating it only on demand.',
    nist_reference: 'MA-4',
  },

  'MA-5': {
    plain_english: 'Only authorized personnel and organizations may perform maintenance on the system; non-escorted access requires appropriate background checks or clearances.',
    example: 'An authorized maintenance personnel list is maintained; vendors not on the list must be escorted by cleared staff at all times and may not access the system without supervision.',
    evidence_tips: [
      'Authorized maintenance personnel and vendor list',
      'Access authorization records for maintenance personnel',
      'Escort policy and records for non-cleared maintenance staff',
    ],
    common_mistakes: 'Allowing any vendor technician to work on systems unsupervised without verifying they are on the authorized list — this creates an insider threat vector.',
    nist_reference: 'MA-5',
  },

  'MA-6': {
    plain_english: 'The organization must have maintenance support arrangements and spare parts available so that critical system components can be repaired or replaced within the defined RTO.',
    example: 'Hardware support contracts guarantee next-business-day parts replacement for critical servers; spare network switches are stocked on-site for immediate failover.',
    evidence_tips: [
      'Maintenance support contracts with response time SLAs',
      'Spare parts inventory for critical components',
      'Evidence that support SLAs align with the system\'s defined RTO',
    ],
    common_mistakes: 'Having a defined RTO of 4 hours but no maintenance contract guaranteeing parts within that window — the RTO becomes unachievable.',
    nist_reference: 'MA-6',
  },

  // ── MEDIA PROTECTION (MP) ─────────────────────────────────────────────────

  'MP-1': {
    plain_english: 'You need a written media protection policy covering purpose, scope, roles, and responsibilities, plus procedures for how media is handled, stored, transported, and disposed of.',
    example: 'An MP Policy document is reviewed annually and paired with an MP SOP describing labeling, access controls, transport rules, and sanitization procedures for all media types.',
    evidence_tips: [
      'Current approved MP Policy document',
      'Media protection procedures document',
      'Annual review evidence',
    ],
    common_mistakes: 'Having informal media-handling practices with no documented policy — leading to inconsistent handling and potential unauthorized disclosure.',
    nist_reference: 'MP-1',
  },

  'MP-2': {
    plain_english: 'Access to system media — digital and physical — must be restricted to authorized individuals only.',
    example: 'A locked cabinet stores backup tapes; only the storage administrator and ISSO have keys. Access to digital media (S3 buckets, NAS shares) is controlled via RBAC.',
    evidence_tips: [
      'Access control configuration for digital media repositories',
      'Physical access controls for media storage areas (locks, access logs)',
      'List of authorized individuals with media access',
    ],
    common_mistakes: 'Leaving backup media in unlocked server rooms or granting broad access to network shares containing sensitive data.',
    nist_reference: 'MP-2',
  },

  'MP-3': {
    plain_english: 'System media must be marked with security labels that indicate handling restrictions, so anyone handling it knows what protection it requires.',
    example: 'Backup tapes are labeled "CUI — Handle per MP SOP"; USB drives used for data transfers are labeled with classification and owner information.',
    evidence_tips: [
      'Media labeling policy and label examples',
      'Photo evidence of labeled physical media',
      'Digital media classification tagging configuration',
    ],
    common_mistakes: 'Omitting labels on physical media, making it impossible to distinguish sensitive from non-sensitive media during handling or disposal.',
    nist_reference: 'MP-3',
  },

  'MP-4': {
    plain_english: 'Physical media must be stored securely in controlled areas to prevent unauthorized access or theft.',
    example: 'Backup tapes are stored in a locked, access-controlled media vault; digital media is protected via encrypted storage with access logs reviewed monthly.',
    evidence_tips: [
      'Physical media storage location description and access controls',
      'Access log records for media storage areas',
      'Inventory of stored media with custody records',
    ],
    common_mistakes: 'Storing backup media on a shelf in an open office rather than in a locked, controlled space.',
    nist_reference: 'MP-4',
  },

  'MP-5': {
    plain_english: 'When media must be transported outside controlled facilities, it must be protected with encryption or physical security to prevent interception or loss.',
    example: 'Backup tapes transported off-site are encrypted with AES-256 before being sealed in a tamper-evident bag and handed to an authorized courier with chain-of-custody documentation.',
    evidence_tips: [
      'Media transport policy with encryption and chain-of-custody requirements',
      'Encryption configuration for media transported outside the facility',
      'Chain-of-custody records for media shipments',
    ],
    common_mistakes: 'Transporting unencrypted backup media in a courier bag — a single lost shipment can result in a reportable data breach.',
    nist_reference: 'MP-5',
  },

  'MP-6': {
    plain_english: 'Before disposing of or reusing media, it must be sanitized using approved methods (overwrite, degauss, or physical destruction) to prevent data recovery.',
    example: 'Decommissioned hard drives are wiped with a DoD 5220.22-M compliant tool and the certificate of sanitization is retained; drives that cannot be wiped are physically shredded.',
    evidence_tips: [
      'Media sanitization policy referencing approved methods (NIST SP 800-88)',
      'Certificates of sanitization or destruction for disposed media',
      'Vendor disposal contracts for bulk media destruction',
    ],
    common_mistakes: 'Simply deleting files or reformatting a drive before disposal — standard deletion does not prevent forensic data recovery.',
    nist_reference: 'MP-6',
  },

  'MP-7': {
    plain_english: 'The organization must restrict or prohibit the use of portable storage devices on external systems to prevent data exfiltration or malware introduction.',
    example: 'USB ports are disabled via GPO on all managed endpoints; only security-approved encrypted USB drives are whitelisted and their use is logged.',
    evidence_tips: [
      'Endpoint configuration showing USB port restriction or whitelist',
      'Policy prohibiting unauthorized portable storage use',
      'Monitoring/DLP alert configuration for removable media events',
    ],
    common_mistakes: 'Relying on a policy that prohibits personal USB drives without any technical enforcement — users will use them anyway without controls.',
    nist_reference: 'MP-7',
  },

  'MP-8': {
    plain_english: 'Before releasing CUI media to the public, it must be downgraded through an approved process to ensure sensitive information has been removed.',
    example: 'Before releasing a backup to a public records request, the ISSO reviews and applies an approved redaction and declassification process per agency policy.',
    evidence_tips: [
      'Media downgrading procedures document',
      'Records of downgrading actions taken before public release',
      'Approval documentation for each downgraded media release',
    ],
    common_mistakes: 'Releasing media without a formal review process, inadvertently disclosing residual CUI that was not completely removed.',
    nist_reference: 'MP-8',
  },

  // ── PHYSICAL AND ENVIRONMENTAL PROTECTION (PE) ───────────────────────────

  'PE-1': {
    plain_english: 'You need a written physical and environmental protection policy covering purpose, scope, roles, and responsibilities, plus procedures for how physical access is controlled and monitored.',
    example: 'A PE Policy is reviewed annually and paired with a Physical Security SOP describing badge access provisioning, visitor handling, and environmental monitoring procedures.',
    evidence_tips: [
      'Current approved PE Policy document',
      'Physical security procedures document',
      'Annual review evidence',
    ],
    common_mistakes: 'Relying on a data center vendor\'s physical security without documenting your own organization\'s physical security requirements and responsibilities.',
    nist_reference: 'PE-1',
  },

  'PE-2': {
    plain_english: 'The organization must maintain a list of individuals authorized to access the facility, verify credentials at entry, and review the list at a defined frequency.',
    example: 'A badge access roster is reviewed quarterly; terminated employees are removed from the list within 24 hours of departure and their badge access is revoked.',
    evidence_tips: [
      'Current authorized access list with last-review date',
      'Badge revocation records for departed employees',
      'Quarterly access list review records',
    ],
    common_mistakes: 'Failing to promptly revoke facility access for departed employees — a common oversight that leaves physical access open well after departure.',
    nist_reference: 'PE-2',
  },

  'PE-3': {
    plain_english: 'The organization must enforce physical access controls at all entry and exit points, verifying authorization before allowing anyone into controlled areas.',
    example: 'All server room entry points use badge readers with PIN; access attempts are logged; security staff verify credentials for any after-hours access.',
    evidence_tips: [
      'Physical access control system configuration and logs',
      'Evidence that all entry/exit points are covered (no unmonitored doors)',
      'Records of access attempt reviews',
    ],
    common_mistakes: 'Securing the main entrance but leaving secondary doors (fire exits, loading docks) without monitored access controls.',
    nist_reference: 'PE-3',
  },

  'PE-4': {
    plain_english: 'Physical access to network distribution and transmission lines — cabling, patch panels, conduit — must be controlled to prevent tampering or interception.',
    example: 'All network cabling runs through locked conduit; patch panels are housed in locked telecommunications closets accessible only to network staff.',
    evidence_tips: [
      'Physical security documentation for cable runs and wiring closets',
      'Access control records for telecom rooms',
      'Network diagram showing cabling paths within the facility',
    ],
    common_mistakes: 'Leaving wiring closets unlocked or running exposed network cabling through publicly accessible areas like drop ceilings in open offices.',
    nist_reference: 'PE-4',
  },

  'PE-5': {
    plain_english: 'Physical access to printers, monitors, and other output devices must be controlled so that unauthorized individuals cannot view or retrieve sensitive output.',
    example: 'Printers in the secure zone require badge authentication before releasing a print job; monitors in open areas use privacy screens; printed CUI is picked up immediately.',
    evidence_tips: [
      'Printer access control configuration (PIN or badge-release printing)',
      'Privacy screen inventory for workstations in shared spaces',
      'Policy requiring immediate retrieval of printed sensitive documents',
    ],
    common_mistakes: 'Allowing sensitive documents to sit in printer output trays where anyone walking by can pick them up.',
    nist_reference: 'PE-5',
  },

  'PE-6': {
    plain_english: 'Physical access to the facility must be monitored with cameras or security personnel, access logs must be reviewed regularly, and anomalies must be investigated.',
    example: 'CCTV cameras cover all entry points and the server room; the security team reviews access logs weekly and investigates any after-hours or unauthorized access attempts.',
    evidence_tips: [
      'CCTV coverage map and retention period documentation',
      'Physical access log review records',
      'Incident records for physical security anomalies investigated',
    ],
    common_mistakes: 'Installing cameras but never reviewing footage or access logs — physical security monitoring provides no value without active review.',
    nist_reference: 'PE-6',
  },

  'PE-8': {
    plain_english: 'Visitor access records must be maintained for a defined period and reviewed regularly to identify anomalies.',
    example: 'A visitor logbook (or electronic visitor management system) records name, escort, purpose, entry/exit times; logs are retained for 1 year and reviewed monthly.',
    evidence_tips: [
      'Visitor log records with required fields completed',
      'Retention policy for visitor records',
      'Monthly visitor log review records',
    ],
    common_mistakes: 'Keeping a paper visitor log that is never reviewed — anomalous or unauthorized visits go undetected.',
    nist_reference: 'PE-8',
  },

  'PE-9': {
    plain_english: 'Power equipment and cabling must be physically protected from damage, tampering, or destruction that could disrupt system operations.',
    example: 'Power distribution units (PDUs) and UPS systems are housed in locked cabinets; electrical conduit is protected and labeled; power cabling is routed away from high-traffic areas.',
    evidence_tips: [
      'Physical security documentation for power infrastructure',
      'Access controls for electrical rooms and PDU cabinets',
      'Facility inspection records confirming power equipment protection',
    ],
    common_mistakes: 'Leaving power strips and UPS units exposed in shared spaces where accidental or intentional disconnection could bring down production systems.',
    nist_reference: 'PE-9',
  },

  'PE-10': {
    plain_english: 'Emergency power-off capabilities must exist for the system and individual components, and emergency shutoff switches must be protected from accidental activation.',
    example: 'An emergency power-off button in the server room is covered with a protective cage to prevent accidental activation; the location is documented and tested annually.',
    evidence_tips: [
      'Emergency power-off documentation and location records',
      'Evidence of protective covering or guarding for EPO switches',
      'Annual test records confirming EPO functionality',
    ],
    common_mistakes: 'Installing an EPO switch without protection — a single accidental bump can take down the entire data center.',
    nist_reference: 'PE-10',
  },

  'PE-11': {
    plain_english: 'The facility must have an uninterruptible power supply (UPS) to support an orderly shutdown or transition to backup power if the primary power source fails.',
    example: 'UPS systems provide 15 minutes of power for all critical servers during a utility failure, long enough to start diesel generators or perform a clean shutdown.',
    evidence_tips: [
      'UPS specifications and capacity documentation',
      'UPS test records confirming runtime meets requirements',
      'Generator failover test records',
    ],
    common_mistakes: 'Purchasing a UPS without testing it under actual load — rated capacity often does not match real-world performance with full server loads.',
    nist_reference: 'PE-11',
  },

  'PE-12': {
    plain_english: 'Automatic emergency lighting must be in place to illuminate exits and evacuation routes if the power fails.',
    example: 'Battery-backed emergency lights are installed at all server room exits and in evacuation corridors; they are tested quarterly per fire code requirements.',
    evidence_tips: [
      'Emergency lighting installation documentation and coverage map',
      'Quarterly test records confirming lights activate on power failure',
    ],
    common_mistakes: 'Treating emergency lighting as a fire code requirement only, without confirming coverage specifically for the areas where systems and personnel operate.',
    nist_reference: 'PE-12',
  },

  'PE-13': {
    plain_english: 'Automatic fire detection and suppression systems must be in place in the facility and must automatically notify emergency responders if a fire occurs.',
    example: 'The server room has a VESDA early-warning smoke detection system and a clean-agent suppression system that activates automatically and alerts the fire department.',
    evidence_tips: [
      'Fire detection and suppression system documentation',
      'Annual inspection and maintenance records',
      'Evidence of emergency responder notification integration',
    ],
    common_mistakes: 'Using water sprinklers in a server room — water suppression destroys equipment; clean-agent systems are required for electronics.',
    nist_reference: 'PE-13',
  },

  'PE-14': {
    plain_english: 'Temperature and humidity in the facility must be maintained within levels required for safe system operation and monitored continuously.',
    example: 'HVAC maintains server room temperature at 65–75°F and humidity at 40–60% RH; environmental sensors alert the facilities team if thresholds are exceeded.',
    evidence_tips: [
      'Environmental monitoring system configuration and alert thresholds',
      'Historical temperature and humidity logs',
      'HVAC maintenance records',
    ],
    common_mistakes: 'Monitoring temperature but not humidity — high humidity causes condensation and corrosion; low humidity causes electrostatic discharge damage.',
    nist_reference: 'PE-14',
  },

  'PE-15': {
    plain_english: 'The facility must be protected against water damage, with accessible master shutoff or isolation valves that work properly.',
    example: 'The server room has no water pipes overhead; a water leak detection sensor alerts facilities staff; the master water shutoff valve location is documented and tested annually.',
    evidence_tips: [
      'Water leak detection sensor placement documentation',
      'Master shutoff valve location map and accessibility confirmation',
      'Annual valve operability test records',
    ],
    common_mistakes: 'Running water pipes directly above server equipment — a single pipe burst can destroy irreplaceable hardware and data.',
    nist_reference: 'PE-15',
  },

  'PE-16': {
    plain_english: 'Removal of system components from the facility must be authorized and monitored, with records maintained of what was removed and by whom.',
    example: 'Any equipment leaving the facility requires a removal authorization form signed by the ISSO; security staff verify the authorization before allowing the equipment out.',
    evidence_tips: [
      'Equipment removal authorization records',
      'Log of assets removed from the facility',
      'Return or disposal records for removed equipment',
    ],
    common_mistakes: 'Allowing employees to take equipment home without formal authorization or documentation — creating both asset tracking and data security gaps.',
    nist_reference: 'PE-16',
  },

  'PE-17': {
    plain_english: 'When employees work from alternate locations (home offices, remote sites), additional security safeguards must protect sensitive processing at those sites.',
    example: 'Remote employees must use a VPN, store no CUI locally, use a company-managed device, and have a private workspace; they acknowledge these requirements annually.',
    evidence_tips: [
      'Alternate work site security policy',
      'Employee acknowledgment records for remote work security requirements',
      'Technical controls enforcing the policy (MDM, VPN requirement)',
    ],
    common_mistakes: 'Treating home offices as fully trusted environments equivalent to the secure facility, without implementing any compensating controls.',
    nist_reference: 'PE-17',
  },

  'PE-18': {
    plain_english: 'System components should be physically positioned to minimize exposure to environmental hazards and reduce the opportunity for unauthorized physical access.',
    example: 'Servers are positioned away from exterior walls and windows; cable management prevents tripping hazards; racks are arranged so that maintenance access does not expose neighboring equipment.',
    evidence_tips: [
      'Facility floor plan showing component placement rationale',
      'Documentation of siting decisions relative to hazards',
    ],
    common_mistakes: 'Placing critical servers directly below water pipes or against exterior walls without considering flood or intrusion risk.',
    nist_reference: 'PE-18',
  },

  'PE-19': {
    plain_english: 'The organization must protect systems from electromagnetic signal emanations that could be intercepted to reconstruct sensitive information.',
    example: 'Systems processing classified information are shielded per TEMPEST requirements; commercial cloud environments rely on multi-tenant isolation and physical separation controls.',
    evidence_tips: [
      'Emanation security documentation or TEMPEST assessment results',
      'Physical shielding specifications for sensitive processing areas',
    ],
    common_mistakes: 'Ignoring electromagnetic emanation risks for systems that process sensitive data in shared or open physical environments.',
    nist_reference: 'PE-19',
  },

  'PE-20': {
    plain_english: 'The organization may employ dynamic and randomized asset management approaches to make it harder for adversaries to predict the location or movement of assets.',
    example: 'Critical assets are periodically redistributed across racks and data center zones; asset location information is marked sensitive and access-controlled in the CMDB.',
    evidence_tips: [
      'Asset distribution policy or procedure incorporating randomization',
      'Access controls on asset location data in CMDB',
    ],
    common_mistakes: 'Publishing detailed rack diagrams or asset locations in openly accessible systems, making it easy for adversaries to plan targeted physical attacks.',
    nist_reference: 'PE-20',
  },

  // ── PLANNING (PL) ─────────────────────────────────────────────────────────

  'PL-1': {
    plain_english: 'You need a written planning policy covering purpose, scope, roles, and responsibilities, plus procedures for how security and privacy planning activities are conducted.',
    example: 'A Planning Policy is published, reviewed annually, and paired with a Planning SOP describing how the SSP is developed, updated, and reviewed.',
    evidence_tips: [
      'Current approved PL Policy document',
      'Planning procedures document',
      'Annual review evidence',
    ],
    common_mistakes: 'Treating the SSP as a one-time ATO artifact rather than a living document governed by a formal planning policy.',
    nist_reference: 'PL-1',
  },

  'PL-2': {
    plain_english: 'The organization must develop, maintain, and update a System Security Plan (SSP) that fully describes the system, its security requirements, and the controls in place to meet them.',
    example: 'The SSP is maintained in the ATO repository, updated within 30 days of any significant system change, and reviewed by the ISSO annually.',
    evidence_tips: [
      'Current SSP document with approval date and version',
      'SSP update records tied to system changes',
      'Annual SSP review evidence',
    ],
    common_mistakes: 'Submitting an SSP for ATO and never updating it — after system changes, the SSP no longer accurately describes the system under authorization.',
    nist_reference: 'PL-2',
  },

  'PL-4': {
    plain_english: 'Users must be given and must acknowledge rules of behavior that define their responsibilities and acceptable use of organizational systems.',
    example: 'All users sign a Rules of Behavior (RoB) document before their account is activated; the RoB is re-acknowledged annually and updated when policies change.',
    evidence_tips: [
      'Rules of Behavior document with current version',
      'Signed RoB acknowledgment records for all users',
      'Annual re-acknowledgment records',
    ],
    common_mistakes: 'Having users sign the RoB only once at hire and never requiring re-acknowledgment, even when the rules are updated.',
    nist_reference: 'PL-4',
  },

  'PL-7': {
    plain_english: 'For systems containing PII, the organization must develop a Concept of Operations (CONOPS) document describing the system\'s privacy posture and how PII is managed.',
    example: 'A Privacy CONOPS document describes what PII the system collects, why it is needed, how it is protected, and how long it is retained.',
    evidence_tips: [
      'Privacy CONOPS document with current approval date',
      'Evidence of periodic review and update',
    ],
    common_mistakes: 'Covering PII handling only in the Privacy Impact Assessment without a separate CONOPS that addresses operational security and privacy posture.',
    nist_reference: 'PL-7',
  },

  'PL-8': {
    plain_english: 'The organization must develop and document a security and privacy architecture that describes how security requirements are met and why the design decisions were made.',
    example: 'A Security Architecture document describes network segmentation, encryption design, authentication flows, and the rationale for key design choices aligned to FedRAMP requirements.',
    evidence_tips: [
      'Security and privacy architecture document',
      'Architecture diagrams linked to SSP control implementations',
      'Evidence of architecture review at a defined frequency',
    ],
    common_mistakes: 'Describing the architecture only at a high level without documenting the security rationale — assessors need to understand why design choices were made.',
    nist_reference: 'PL-8',
  },

  'PL-9': {
    plain_english: 'Security and privacy controls must be centrally managed and coordinated across the entire organization, not implemented in silos.',
    example: 'A central GRC platform tracks all control implementations, and the ISSO coordinates with business units quarterly to ensure consistent control application across systems.',
    evidence_tips: [
      'Evidence of centralized control management tool or process',
      'Cross-system control coordination meeting records',
    ],
    common_mistakes: 'Allowing each system team to implement controls independently without coordination, resulting in inconsistent control quality and gaps.',
    nist_reference: 'PL-9',
  },

  'PL-10': {
    plain_english: 'Security and privacy requirements must be integrated into the system development lifecycle from the start, not bolted on after development.',
    example: 'Security requirements are defined during the Initiation phase of the SDLC; security gates in the CI/CD pipeline enforce controls before code reaches production.',
    evidence_tips: [
      'SDLC documentation showing security integration points',
      'Security requirements traceability matrix',
      'CI/CD pipeline security gate configuration',
    ],
    common_mistakes: 'Treating security as a final checkbox before deployment rather than a requirement that shapes design and development from day one.',
    nist_reference: 'PL-10',
  },

  'PL-11': {
    plain_english: 'The organization must identify and document the types of information processed by the system to support data classification and privacy risk management.',
    example: 'An information type inventory lists all data categories processed (e.g., PII, financial data, health information) with classification levels and associated handling requirements.',
    evidence_tips: [
      'Information type inventory document with data categories and classifications',
      'Mapping of information types to applicable handling requirements',
      'Evidence of inventory review at a defined frequency',
    ],
    common_mistakes: 'Failing to maintain a complete inventory of information types, which leads to missed privacy obligations and misapplied security controls.',
    nist_reference: 'PL-11',
  },

  // ── PERSONNEL SECURITY (PS) ───────────────────────────────────────────────

  'PS-1': {
    plain_english: 'You need a written personnel security policy covering purpose, scope, roles, and responsibilities, plus procedures for screening, onboarding, and offboarding personnel.',
    example: 'A PS Policy is reviewed annually and paired with an HR Security SOP describing background check requirements, access agreements, and offboarding procedures.',
    evidence_tips: [
      'Current approved PS Policy document',
      'Personnel security procedures document',
      'Annual review evidence',
    ],
    common_mistakes: 'Treating personnel security as purely an HR function without a formal policy that addresses information system access implications.',
    nist_reference: 'PS-1',
  },

  'PS-2': {
    plain_english: 'Every organizational position must be assigned a risk designation that determines the required level of background investigation before the person in that role can access systems.',
    example: 'Position risk designations are documented in job descriptions: IT admins are "High Risk" requiring a Tier 4 investigation; regular users are "Low Risk" requiring a Tier 1.',
    evidence_tips: [
      'Position risk designation matrix or job description annotations',
      'Evidence of annual review of position risk designations',
      'Background investigation records matched to risk level requirements',
    ],
    common_mistakes: 'Assigning all positions the same low-risk designation to simplify screening, rather than applying higher scrutiny to privileged or sensitive roles.',
    nist_reference: 'PS-2',
  },

  'PS-3': {
    plain_english: 'Individuals must be screened before being granted system access, and rescreened if their role changes or if defined conditions occur (e.g., a certain number of years pass).',
    example: 'All new hires complete a background check before system access is provisioned; privileged users are rescreened every 5 years or if a security concern arises.',
    evidence_tips: [
      'Background check completion records for all current users',
      'Rescreening schedule and records',
      'Policy defining conditions that trigger rescreening',
    ],
    common_mistakes: 'Screening employees at hire but never rescreening, even for highly privileged roles where risk increases with tenure and access accumulation.',
    nist_reference: 'PS-3',
  },

  'PS-4': {
    plain_english: 'When an employee leaves, their system access must be immediately terminated, organizational property returned, and relevant personnel notified.',
    example: 'The offboarding checklist requires IT to disable all accounts within 4 hours of an employee\'s last day; a termination notification is sent to the ISSO and their manager.',
    evidence_tips: [
      'Offboarding checklist with account termination step and completion records',
      'Access termination records showing timing relative to departure date',
      'Equipment return records',
    ],
    common_mistakes: 'Relying on HR to notify IT of departures days or weeks after the fact — departed employee accounts remaining active is a critical and common vulnerability.',
    nist_reference: 'PS-4',
  },

  'PS-5': {
    plain_english: 'When employees transfer roles or are reassigned, their system access must be reviewed, modified to reflect the new role, and excess permissions revoked.',
    example: 'When a developer moves to a management role, IT reviews their access within 5 business days, removes developer-level privileges, and grants the appropriate manager-level access.',
    evidence_tips: [
      'Transfer notification process documentation',
      'Access review and modification records linked to transfer events',
      'Evidence that old permissions are revoked promptly after transfer',
    ],
    common_mistakes: 'Granting new access for a transferred employee without removing their old access — allowing privilege accumulation over time.',
    nist_reference: 'PS-5',
  },

  'PS-6': {
    plain_english: 'All individuals requiring access to organizational systems must sign an access agreement (e.g., rules of behavior, NDA) before access is granted, reviewed, and updated at a defined frequency.',
    example: 'Every user signs an Access Agreement before their account is created; agreements are re-signed annually and whenever the terms are updated.',
    evidence_tips: [
      'Current Access Agreement document with version date',
      'Signed agreement records for all current users',
      'Annual re-signature records or attestation logs',
    ],
    common_mistakes: 'Collecting signatures only at initial access grant without a re-acknowledgment process — users forget their obligations and agreements become outdated.',
    nist_reference: 'PS-6',
  },

  'PS-7': {
    plain_english: 'External service providers with access to organizational systems must meet the same personnel security requirements as internal staff, and the organization must monitor their compliance.',
    example: 'Contractor agreements require background checks equivalent to internal staff; the ISSO verifies contractor screening compliance annually and is notified within 24 hours when a contractor\'s access should be revoked.',
    evidence_tips: [
      'Third-party contract language specifying personnel security requirements',
      'Contractor background check compliance records',
      'Evidence of provider notification when contractor personnel change',
    ],
    common_mistakes: 'Applying rigorous screening to employees but allowing contractors to self-certify their background checks without verification.',
    nist_reference: 'PS-7',
  },

  'PS-8': {
    plain_english: 'The organization must have a formal sanctions process for personnel who violate security or privacy policies, and relevant personnel must be notified when sanctions are applied.',
    example: 'The sanctions policy defines a tiered response from written warning through termination depending on violation severity; HR and the ISSO are notified for all sanctions.',
    evidence_tips: [
      'Formal sanctions policy with defined consequences',
      'Records of sanctions applied for policy violations',
      'Evidence of ISSO notification for security-related sanctions',
    ],
    common_mistakes: 'Having no documented consequences for policy violations, making it impossible to consistently enforce security requirements.',
    nist_reference: 'PS-8',
  },

  'PS-9': {
    plain_english: 'The organization must protect systems and information during personnel actions — including terminations, transfers, and disciplinary situations — to prevent insider threats.',
    example: 'For involuntary terminations, IT is notified simultaneously with HR before the employee is informed, ensuring access is revoked before the employee is aware of their termination.',
    evidence_tips: [
      'Involuntary termination security procedures',
      'Records showing simultaneous IT and HR notification for sensitive departures',
      'Evidence of immediate access revocation in high-risk termination cases',
    ],
    common_mistakes: 'Notifying IT of an involuntary termination after the employee has already been told — giving a disgruntled employee a window to damage or exfiltrate data.',
    nist_reference: 'PS-9',
  },

  // ── RISK ASSESSMENT (RA) ──────────────────────────────────────────────────

  'RA-1': {
    plain_english: 'You need a written risk assessment policy covering purpose, scope, roles, and responsibilities, plus procedures for how risk assessments are conducted and maintained.',
    example: 'An RA Policy is reviewed annually and paired with an RA SOP describing risk assessment methodology, frequency, and reporting requirements.',
    evidence_tips: [
      'Current approved RA Policy document',
      'Risk assessment procedures document',
      'Annual review evidence',
    ],
    common_mistakes: 'Treating risk assessment as a one-time ATO activity rather than an ongoing program governed by a formal policy.',
    nist_reference: 'RA-1',
  },

  'RA-2': {
    plain_english: 'The organization must formally categorize the system and its information using FIPS 199 / NIST SP 800-60 to establish the appropriate baseline of security controls.',
    example: 'An FIPS 199 categorization document assigns Confidentiality, Integrity, and Availability impact levels (Low/Moderate/High) for the system, reviewed and approved by the AO.',
    evidence_tips: [
      'FIPS 199 security categorization document with AO approval',
      'NIST SP 800-60 information type mapping',
      'Evidence of categorization review when system changes occur',
    ],
    common_mistakes: 'Underestimating impact levels to avoid more stringent controls — if a breach would cause serious harm, the system must be categorized accordingly.',
    nist_reference: 'RA-2',
  },

  'RA-3': {
    plain_english: 'The organization must conduct risk assessments that identify threats and vulnerabilities, estimate likelihood and impact, and produce a report with findings and recommendations.',
    example: 'An annual risk assessment uses NIST SP 800-30 methodology; the resulting Risk Assessment Report is reviewed by leadership and feeds into the POA&M.',
    evidence_tips: [
      'Risk Assessment Report with methodology, findings, and recommendations',
      'Evidence of annual assessment cycle',
      'Linkage between risk assessment findings and POA&M entries',
    ],
    common_mistakes: 'Confusing a vulnerability scan with a risk assessment — scanning identifies vulnerabilities but does not assess likelihood, impact, or risk to the organization.',
    nist_reference: 'RA-3',
  },

  'RA-5': {
    plain_english: 'The organization must conduct vulnerability scans of the system at a defined frequency, when significant changes occur, and remediate findings according to risk.',
    example: 'Authenticated vulnerability scans run weekly against all system components; critical and high findings are remediated within 30 days per the vulnerability management policy.',
    evidence_tips: [
      'Vulnerability scan reports with scan dates and scope',
      'Remediation tracking records showing finding age and closure dates',
      'Scan tool configuration confirming authentication is used',
    ],
    common_mistakes: 'Running unauthenticated scans that miss most vulnerabilities, or scanning but having no defined remediation SLA tied to severity.',
    nist_reference: 'RA-5',
  },

  'RA-7': {
    plain_english: 'The organization must respond to findings from risk assessments and security monitoring by implementing security measures to address identified weaknesses.',
    example: 'Assessment findings are entered into the POA&M within 5 days; the ISSO assigns owners and tracks remediation to closure, reporting status to the AO monthly.',
    evidence_tips: [
      'POA&M entries created from risk assessment findings',
      'Remediation evidence for closed findings',
      'Monthly risk status reports to the AO',
    ],
    common_mistakes: 'Producing risk assessment reports without a formal process to act on the findings — reports that sit unreviewed provide no security benefit.',
    nist_reference: 'RA-7',
  },

  'RA-8': {
    plain_english: 'Information about security weaknesses must be reported to designated officials and protected from unauthorized disclosure to prevent exploitation.',
    example: 'Vulnerability and weakness reports are classified as sensitive, shared only with the ISSO, AO, and authorized stakeholders, and stored in an access-controlled repository.',
    evidence_tips: [
      'Access controls on vulnerability and risk assessment reports',
      'Distribution list for security weakness notifications',
      'Policy specifying who receives and handles weakness information',
    ],
    common_mistakes: 'Publishing detailed vulnerability information in broadly accessible systems where adversaries or unauthorized users could access it.',
    nist_reference: 'RA-8',
  },

  'RA-9': {
    plain_english: 'The organization must determine and document the criticality of the system to missions and business functions, to inform risk-based prioritization decisions.',
    example: 'A system criticality analysis documents that the system supports Tier 1 mission functions; this drives the selection of Moderate baseline controls and prioritizes its recovery.',
    evidence_tips: [
      'System criticality analysis or Business Impact Analysis (BIA)',
      'Evidence that criticality informed control selection and RTO/RPO targets',
    ],
    common_mistakes: 'Treating all systems as equally critical — without differentiated criticality, security investments cannot be prioritized based on mission impact.',
    nist_reference: 'RA-9',
  },

  // ── SYSTEM AND SERVICES ACQUISITION (SA) ─────────────────────────────────

  'SA-1': {
    plain_english: 'You need a written system and services acquisition policy covering purpose, scope, roles, and responsibilities, plus procedures for how security requirements are incorporated into acquisitions.',
    example: 'An SA Policy is reviewed annually and paired with an acquisition SOP requiring security reviews and contract language for all system and service procurements.',
    evidence_tips: [
      'Current approved SA Policy document',
      'Acquisition security procedures document',
      'Annual review evidence',
    ],
    common_mistakes: 'Treating security as an afterthought in acquisitions, adding it after contract negotiations instead of building it into requirements from the start.',
    nist_reference: 'SA-1',
  },

  'SA-2': {
    plain_english: 'Security resources must be explicitly identified, allocated, and funded as part of the capital planning process — not treated as an optional line item.',
    example: 'The annual budget request includes a dedicated security line item covering SIEM licensing, penetration testing, and the ISSO role; this is reviewed and approved as part of IT investment planning.',
    evidence_tips: [
      'Budget documentation showing security-specific resource allocation',
      'Capital planning documents referencing security requirements',
      'Evidence of AO or CISO review of security resource adequacy',
    ],
    common_mistakes: 'Funding security reactively after incidents rather than proactively budgeting for the controls required by the system\'s risk profile.',
    nist_reference: 'SA-2',
  },

  'SA-3': {
    plain_english: 'The system must be developed and maintained using a formal System Development Life Cycle (SDLC) that integrates security and privacy at every phase.',
    example: 'The organization uses an Agile SDLC with security gates at each sprint; security requirements are defined in the backlog, reviewed in sprint planning, and validated before release.',
    evidence_tips: [
      'SDLC documentation showing security integration at each phase',
      'Security role assignments for each SDLC phase',
      'Evidence of security activities (threat modeling, code review) in the SDLC',
    ],
    common_mistakes: 'Running development without security involvement until a final pre-deployment security review — by then, expensive architectural changes may be required.',
    nist_reference: 'SA-3',
  },

  'SA-4': {
    plain_english: 'Security and privacy requirements must be explicitly included in contracts and acquisition documents for systems, components, and services.',
    example: 'All vendor contracts include a Security Requirements Appendix specifying required controls, audit rights, incident notification obligations, and FedRAMP equivalency requirements.',
    evidence_tips: [
      'Contract templates with security requirements language',
      'Signed contracts showing security requirements included',
      'Vendor compliance verification records',
    ],
    common_mistakes: 'Using boilerplate contracts without security-specific language, leaving the organization with no contractual basis to enforce security requirements on vendors.',
    nist_reference: 'SA-4',
  },

  'SA-5': {
    plain_english: 'The organization must obtain and protect documentation for systems and components — including administrator guides and security documentation — and make it available to authorized personnel.',
    example: 'System documentation, including security guides and architecture documents, is stored in an access-controlled repository and reviewed for completeness during each assessment.',
    evidence_tips: [
      'Documentation repository with access controls',
      'Inventory of documentation received from developers/vendors',
      'Evidence that documentation is current and accessible to authorized staff',
    ],
    common_mistakes: 'Accepting systems without requiring complete security documentation, making it impossible to correctly configure or assess the system.',
    nist_reference: 'SA-5',
  },

  'SA-8': {
    plain_english: 'Security and privacy engineering principles must be applied throughout the design, development, and modification of the system.',
    example: 'The development team applies NIST SP 800-160 engineering principles; threat modeling is performed during design, and security requirements are traced through implementation.',
    evidence_tips: [
      'Security engineering principles documentation or policy',
      'Threat modeling artifacts from the design phase',
      'Security requirements traceability matrix',
    ],
    common_mistakes: 'Applying security controls as add-ons to an existing design rather than engineering security in from the beginning.',
    nist_reference: 'SA-8',
  },

  'SA-9': {
    plain_english: 'External service providers must be required to comply with applicable security laws, regulations, and the organization\'s security requirements, documented in contracts.',
    example: 'All cloud service provider contracts specify FedRAMP authorization requirements, incident notification SLAs, and the right-to-audit clause.',
    evidence_tips: [
      'Contracts with external providers specifying security requirements',
      'FedRAMP authorization status records for cloud providers',
      'Annual compliance verification or assessment records for providers',
    ],
    common_mistakes: 'Using cloud services or SaaS tools that have not been evaluated for compliance, assuming FedRAMP authorization without verification.',
    nist_reference: 'SA-9',
  },

  'SA-10': {
    plain_english: 'Developers must maintain a configuration management plan for system development that controls changes, tracks security flaws, and documents flaw resolution.',
    example: 'The developer\'s SDLC CM plan covers code branching strategy, security flaw tracking in Jira, and requires security sign-off before merging to main.',
    evidence_tips: [
      'Developer CM plan documentation',
      'Security flaw tracking records with resolution evidence',
      'Code review and approval workflow documentation',
    ],
    common_mistakes: 'Focusing developer CM on functional changes while treating security bug tracking as separate — security flaws need the same rigor as functional defects.',
    nist_reference: 'SA-10',
  },

  'SA-11': {
    plain_english: 'Developers must create and execute a security testing plan throughout development, not just at the end, to find and fix flaws early.',
    example: 'The developer security test plan includes SAST in the CI pipeline, DAST before each release, and a final penetration test before ATO submission.',
    evidence_tips: [
      'Developer security test plan document',
      'SAST/DAST scan results from the development pipeline',
      'Penetration test results before major releases',
    ],
    common_mistakes: 'Relying solely on final pre-release testing — defects found late in the SDLC are far more expensive to fix than those caught during development.',
    nist_reference: 'SA-11',
  },

  'SA-12': {
    plain_english: 'The organization must protect against supply chain threats by vetting suppliers, using trusted sources, and implementing supply chain risk management practices.',
    example: 'A Supply Chain Risk Management Plan governs vendor vetting, component provenance verification, and prohibits hardware from high-risk suppliers for critical components.',
    evidence_tips: [
      'Supply Chain Risk Management Plan document',
      'Vendor vetting records and approved supplier list',
      'Component provenance documentation for critical hardware',
    ],
    common_mistakes: 'Procuring hardware and software from the lowest-cost supplier without any vetting for supply chain integrity or country-of-origin considerations.',
    nist_reference: 'SA-12',
  },

  'SA-15': {
    plain_english: 'Developers must follow a documented development process that explicitly addresses security requirements, identifies flaws early, and uses defined security tools.',
    example: 'The development process document specifies mandatory SAST tool usage, required code review steps, and security acceptance criteria that must be met before each release.',
    evidence_tips: [
      'Developer process documentation with security requirements integrated',
      'Evidence of defined security tools used in development',
      'Security acceptance criteria and sign-off records',
    ],
    common_mistakes: 'Having an ad-hoc development process where security steps are optional or skipped when delivery timelines are tight.',
    nist_reference: 'SA-15',
  },

  'SA-16': {
    plain_english: 'Developers must provide training on the correct use of the security functions and mechanisms they implement so operators know how to configure and use them securely.',
    example: 'The system developer provides a security operations training package covering IAM configuration, audit log management, and incident response integration.',
    evidence_tips: [
      'Developer-provided security training materials',
      'Training completion records for operations staff',
      'Evidence that training covers security functions implemented in the system',
    ],
    common_mistakes: 'Receiving a system with security features enabled but never training operators on how to use them correctly — misconfiguration is as dangerous as no security.',
    nist_reference: 'SA-16',
  },

  'SA-17': {
    plain_english: 'Developers must produce a security architecture and design specification detailed enough to allow security testing and evaluation of the system.',
    example: 'The developer provides a Security Architecture document describing authentication flows, encryption implementation, network segmentation, and the rationale for each design choice.',
    evidence_tips: [
      'Developer-provided security architecture document',
      'Design specifications showing security control implementation details',
      'Evidence that assessors used the architecture document during security testing',
    ],
    common_mistakes: 'Accepting high-level architecture diagrams that describe what is protected without describing how it is protected — insufficient for meaningful security evaluation.',
    nist_reference: 'SA-17',
  },

  'SA-21': {
    plain_english: 'Developers and personnel with access to system development must undergo background checks to ensure trustworthiness before performing development work.',
    example: 'All development contractors complete a background investigation equivalent to the system\'s risk tier before being granted access to source code or development environments.',
    evidence_tips: [
      'Background check requirement in developer contracts',
      'Background check completion records for development staff',
      'Process for verifying contractor screening before access is granted',
    ],
    common_mistakes: 'Screening only full-time employees and allowing unvetted contractors into the development environment — contractors represent the same insider threat risk.',
    nist_reference: 'SA-21',
  },

  'SA-22': {
    plain_english: 'When vendor support for a system component ends, the organization must replace it or formally document and accept the risk of continuing to use an unsupported component.',
    example: 'An EOL tracking register flags components approaching end of support 12 months in advance; unsupported components require a Risk Acceptance signed by the AO before continued use.',
    evidence_tips: [
      'End-of-life/end-of-support tracking register',
      'Replacement plans or migration timelines for EOL components',
      'Risk acceptance documentation for any unsupported components currently in use',
    ],
    common_mistakes: 'Continuing to use unsupported software without documenting the risk, creating an undisclosed vulnerability that assessors will flag as a significant finding.',
    nist_reference: 'SA-22',
  },

  'SA-4(1)': {
    plain_english: 'Acquisition contracts must require developers to describe the functional properties of security controls in sufficient detail to allow analysis and testing.',
    example: 'The acquisition contract requires the vendor to provide a Functional Security Description document for each security control, describing inputs, outputs, and expected behaviors.',
    evidence_tips: [
      'Contract language requiring functional security control descriptions',
      'Functional security description documents received from the vendor',
    ],
    common_mistakes: 'Accepting vague statements like "the system uses industry-standard encryption" without requiring specifics about algorithms, key lengths, and implementation.',
    nist_reference: 'SA-4(1)',
  },

  'SA-4(2)': {
    plain_english: 'Acquisition contracts must require developers to provide design and implementation details for security controls detailed enough to allow testing and evaluation.',
    example: 'The contract requires design documentation showing how encryption is implemented in the data layer, including key management architecture and cipher suite selection.',
    evidence_tips: [
      'Contract language requiring design/implementation security documentation',
      'Design documentation received and used during security assessment',
    ],
    common_mistakes: 'Receiving only marketing-level security descriptions — assessors need implementation details to evaluate whether controls actually provide the claimed protection.',
    nist_reference: 'SA-4(2)',
  },

  'SA-4(9)': {
    plain_english: 'Developers must identify early in the SDLC the functions, ports, protocols, and services the system will use, supporting least-privilege configuration planning.',
    example: 'During the design phase, developers produce a Ports, Protocols, and Services (PPS) register that is reviewed by the ISSO and used to configure firewall rules.',
    evidence_tips: [
      'Ports, Protocols, and Services register produced during design',
      'Evidence that the PPS register is used to configure network controls',
      'Change process for adding new PPS entries',
    ],
    common_mistakes: 'Discovering required ports and protocols only after deployment, resulting in overly permissive firewall rules opened as workarounds.',
    nist_reference: 'SA-4(9)',
  },

  'SA-9(1)': {
    plain_english: 'Before outsourcing dedicated security services, the organization must conduct a risk assessment to ensure the decision aligns with risk management priorities.',
    example: 'Before contracting an MSSP for SOC services, a risk assessment evaluates the MSSP\'s security posture, data handling practices, and ability to meet FedRAMP requirements.',
    evidence_tips: [
      'Risk assessment conducted prior to security service outsourcing decision',
      'Security due diligence records for the selected security service provider',
    ],
    common_mistakes: 'Selecting a security service provider based solely on cost without assessing whether the provider\'s security posture meets organizational requirements.',
    nist_reference: 'SA-9(1)',
  },

  'SA-9(2)': {
    plain_english: 'External service providers must identify the functions, ports, protocols, and services required for delivery of their services so the organization can implement appropriate controls.',
    example: 'Each SaaS vendor provides a documented list of required ports and protocols; the network team opens only those specific paths and blocks all others by default.',
    evidence_tips: [
      'Vendor-provided PPS documentation',
      'Network configuration showing only required vendor connections are permitted',
    ],
    common_mistakes: 'Allowing broad internet access to support vendor connectivity rather than restricting it to the specific protocols and destinations the vendor requires.',
    nist_reference: 'SA-9(2)',
  },

  'SA-9(4)': {
    plain_english: 'The organization must continuously monitor external service providers to ensure they maintain compliance with security requirements over time.',
    example: 'Annual third-party risk reviews verify each provider\'s FedRAMP status, review their most recent SOC 2 report, and assess any significant changes to their security posture.',
    evidence_tips: [
      'Annual third-party provider review records',
      'Monitoring processes (SOC 2 report reviews, FedRAMP status checks)',
      'Records of corrective actions when provider compliance gaps are found',
    ],
    common_mistakes: 'Vetting providers at contract signing but never revisiting their security posture — a provider\'s security can degrade significantly over a multi-year contract.',
    nist_reference: 'SA-9(4)',
  },

  'SA-9(5)': {
    plain_english: 'The organization must restrict where external providers process and store organizational data to specific approved geographic locations.',
    example: 'Contracts prohibit cloud providers from processing or storing federal data outside US-based data centers; the organization verifies data residency in annual reviews.',
    evidence_tips: [
      'Contract language specifying data residency requirements',
      'Provider data residency certifications or attestations',
      'Periodic verification that data remains within approved locations',
    ],
    common_mistakes: 'Assuming data stays in the US without contractual requirements and verification — many cloud providers route data globally by default.',
    nist_reference: 'SA-9(5)',
  },

  // ── SYSTEM AND COMMUNICATIONS PROTECTION (SC) ────────────────────────────

  'SC-1': {
    plain_english: 'You need a written system and communications protection policy covering purpose, scope, roles, and responsibilities, plus procedures for protecting system communications.',
    example: 'An SC Policy is reviewed annually and paired with an SC SOP describing encryption requirements, boundary protection standards, and network segmentation procedures.',
    evidence_tips: [
      'Current approved SC Policy document',
      'Communications protection procedures document',
      'Annual review evidence',
    ],
    common_mistakes: 'Having encryption configured but no governing policy that defines requirements — making it impossible to assess whether the controls are correctly implemented.',
    nist_reference: 'SC-1',
  },

  'SC-2': {
    plain_english: 'The system must separate user-facing functionality from system administration interfaces so that regular users cannot access management functions.',
    example: 'The admin console runs on a separate internal port accessible only via privileged-access workstations; users can only access the application interface on port 443.',
    evidence_tips: [
      'Architecture documentation showing separation of user and admin interfaces',
      'Network or application configuration enforcing the separation',
      'Access control records confirming non-admin users cannot reach the management interface',
    ],
    common_mistakes: 'Hosting the admin panel on the same URL path as the user application, relying solely on authentication to prevent user access to management functions.',
    nist_reference: 'SC-2',
  },

  'SC-3': {
    plain_english: 'Security functions must be isolated from non-security functions to prevent untrusted code or processes from interfering with security mechanisms.',
    example: 'The authentication service runs in a dedicated container with no shared memory with application code; security kernel modules are isolated from user-space processes.',
    evidence_tips: [
      'Architecture documentation showing security function isolation',
      'OS or container configuration enforcing process isolation',
      'Evidence that security functions cannot be modified by unprivileged processes',
    ],
    common_mistakes: 'Mixing security logic (authentication, authorization) into general application code where it can be overridden or bypassed by other application components.',
    nist_reference: 'SC-3',
  },

  'SC-4': {
    plain_english: 'The system must prevent unauthorized information transfer through shared resources — memory, disk, cache — so one user cannot read another\'s residual data.',
    example: 'Memory is zeroed before allocation to new processes; storage volumes are wiped before reassignment; containers use separate namespaces to prevent cross-tenant data leakage.',
    evidence_tips: [
      'OS/platform configuration showing memory zeroing and resource isolation',
      'Multi-tenant isolation architecture documentation',
      'Test evidence confirming cross-tenant data leakage is prevented',
    ],
    common_mistakes: 'In multi-tenant environments, relying on logical separation without verifying that shared physical resources (memory, storage) are properly cleared between users.',
    nist_reference: 'SC-4',
  },

  'SC-5': {
    plain_english: 'The system must protect against or limit the effects of denial-of-service attacks to maintain availability.',
    example: 'A CDN with DDoS protection absorbs volumetric attacks; rate limiting on the API prevents application-layer exhaustion; auto-scaling handles surge traffic.',
    evidence_tips: [
      'DDoS protection service configuration or contract',
      'Rate limiting and throttling configuration',
      'Incident records showing DoS events were detected and mitigated',
    ],
    common_mistakes: 'Relying solely on bandwidth capacity without application-layer rate limiting — a sophisticated attacker can exhaust application resources with low-volume targeted requests.',
    nist_reference: 'SC-5',
  },

  'SC-6': {
    plain_english: 'The system must manage resource availability by allocating resources by priority or quota to prevent any single user or process from exhausting shared resources.',
    example: 'CPU and memory limits are set per container; database connection pools are capped per application tier; storage quotas prevent individual users from filling shared disks.',
    evidence_tips: [
      'Resource quota and limit configuration for compute, memory, and storage',
      'Evidence of resource monitoring and alerting when limits approach',
    ],
    common_mistakes: 'Running all workloads on shared infrastructure without resource quotas — a single runaway process can starve other critical services.',
    nist_reference: 'SC-6',
  },

  'SC-7': {
    plain_english: 'The system must monitor and control communications at external boundaries and key internal boundaries, implementing DMZ architecture for publicly accessible components.',
    example: 'A WAF and firewall enforce the external boundary; an internal firewall separates the web tier from the data tier; all boundary traffic is logged and monitored.',
    evidence_tips: [
      'Network boundary protection architecture diagram',
      'Firewall and WAF rule configurations',
      'Boundary monitoring and alerting configuration',
    ],
    common_mistakes: 'Implementing only external boundary protection without internal segmentation — a compromised front-end system can directly reach the database tier.',
    nist_reference: 'SC-7',
  },

  'SC-8': {
    plain_english: 'Transmitted information must be protected by cryptographic mechanisms or physical safeguards to prevent unauthorized disclosure or modification in transit.',
    example: 'All data in transit uses TLS 1.2 or higher with approved cipher suites; internal service-to-service traffic uses mTLS; no plaintext protocols are permitted.',
    evidence_tips: [
      'TLS configuration showing version and cipher suite requirements',
      'Network scan results confirming no plaintext protocols are in use',
      'Certificate management documentation',
    ],
    common_mistakes: 'Enforcing encryption on external connections while allowing internal service communications to travel unencrypted over the network.',
    nist_reference: 'SC-8',
  },

  'SC-10': {
    plain_english: 'Network connections must be terminated after a session ends or after a defined period of inactivity to free resources and prevent session hijacking.',
    example: 'TCP connections are terminated after sessions close or after 5 minutes of inactivity at the load balancer; idle database connections are recycled after 10 minutes.',
    evidence_tips: [
      'Load balancer and firewall idle timeout configuration',
      'Application session timeout configuration',
      'Evidence that idle connections are properly terminated',
    ],
    common_mistakes: 'Allowing connections to persist indefinitely, creating opportunities for session hijacking and resource exhaustion.',
    nist_reference: 'SC-10',
  },

  'SC-11': {
    plain_english: 'The system must provide a trusted communication path between users and security functions that protects those communications from interception or modification.',
    example: 'All admin operations are performed over an encrypted, MFA-protected VPN tunnel; the admin console uses certificate pinning to prevent MITM interception.',
    evidence_tips: [
      'Trusted path architecture documentation',
      'VPN or encrypted channel configuration for admin access',
      'Evidence of certificate pinning or similar trusted-path enforcement',
    ],
    common_mistakes: 'Allowing admin operations over the same network path as user traffic — a compromised user session cannot be allowed to intercept admin communications.',
    nist_reference: 'SC-11',
  },

  'SC-12': {
    plain_english: 'Cryptographic keys must be managed throughout their lifecycle — generation, distribution, storage, rotation, and destruction — using automated mechanisms where possible.',
    example: 'AWS KMS manages all encryption keys with automatic annual rotation; key access is logged; compromised keys are revoked and replaced within 24 hours.',
    evidence_tips: [
      'Key management policy and procedures',
      'KMS or HSM configuration showing key lifecycle management',
      'Key rotation records and access logs',
    ],
    common_mistakes: 'Using long-lived static encryption keys that are never rotated — if a key is compromised, all data encrypted with it is exposed retroactively.',
    nist_reference: 'SC-12',
  },

  'SC-13': {
    plain_english: 'All cryptography used in the system must be FIPS 140-2/3 validated or NSA-approved — not just any encryption implementation.',
    example: 'The system uses OpenSSL built with the FIPS provider enabled; all encryption operations are performed by the FIPS-validated module documented in the SSP.',
    evidence_tips: [
      'FIPS 140-2/3 validation certificates for all cryptographic modules',
      'SSP documentation mapping each cryptographic use to a validated module',
      'Scan or configuration review confirming no non-FIPS algorithms are in use',
    ],
    common_mistakes: 'Using popular encryption libraries without verifying they are operating in FIPS mode — most libraries support non-FIPS algorithms by default.',
    nist_reference: 'SC-13',
  },

  'SC-15': {
    plain_english: 'Remote activation of microphones, cameras, or other collaborative computing devices must be prohibited, with visible indicators when they are in use.',
    example: 'Conferencing software cannot activate the microphone or camera without explicit user consent; a hardware LED indicator illuminates whenever the camera is active.',
    evidence_tips: [
      'Policy prohibiting unauthorized remote device activation',
      'Technical configuration or hardware controls preventing remote activation',
      'Evidence of user-visible indicators for active collaborative devices',
    ],
    common_mistakes: 'Using videoconferencing software that can be activated remotely without user action or visible indication — creating covert surveillance risks.',
    nist_reference: 'SC-15',
  },

  'SC-16': {
    plain_english: 'Security and privacy attributes must be associated with information exchanged between systems so that receiving systems can enforce appropriate policies on that information.',
    example: 'Documents are tagged with classification metadata in transit; the receiving system reads the tag and applies corresponding handling controls automatically.',
    evidence_tips: [
      'Data exchange protocol documentation showing attribute tagging',
      'Receiving system configuration showing attribute-based policy enforcement',
    ],
    common_mistakes: 'Exchanging data between systems without carrying classification labels — receiving systems cannot enforce correct handling without knowing the data\'s sensitivity.',
    nist_reference: 'SC-16',
  },

  'SC-17': {
    plain_english: 'Public key certificates must be issued by an appropriate certificate authority (CA) under a certificate policy, or obtained from a FedRAMP-approved provider.',
    example: 'All TLS certificates are issued by a FedRAMP-authorized CA under the Federal PKI hierarchy; self-signed certificates are prohibited in production.',
    evidence_tips: [
      'Certificate inventory showing issuing CA for all certificates',
      'Certificate policy or procedure requiring approved CAs',
      'Evidence that no self-signed certificates are in use in production',
    ],
    common_mistakes: 'Using self-signed certificates in production — they cannot be independently validated and may be replaced by attackers performing MITM attacks.',
    nist_reference: 'SC-17',
  },

  'SC-18': {
    plain_english: 'The organization must define acceptable and unacceptable mobile code, establish usage restrictions, and control its use within the system.',
    example: 'A mobile code policy defines approved JavaScript frameworks and prohibits unsigned ActiveX and Java applets; browser security settings enforce the restrictions.',
    evidence_tips: [
      'Mobile code policy with approved and prohibited code types',
      'Browser and endpoint configuration enforcing mobile code restrictions',
      'Evidence of review when new mobile code types are introduced',
    ],
    common_mistakes: 'Allowing any mobile code to execute without restriction — malicious JavaScript or ActiveX can execute with user-level permissions and exfiltrate data.',
    nist_reference: 'SC-18',
  },

  'SC-19': {
    plain_english: 'VoIP technologies must be governed by usage restrictions and authorized before deployment, with usage monitored.',
    example: 'An approved VoIP platform is specified in policy; shadow VoIP applications are prohibited; VoIP traffic is isolated in a dedicated VLAN with QoS controls.',
    evidence_tips: [
      'VoIP usage policy and approved platform list',
      'Network configuration showing VoIP VLAN isolation',
      'Evidence of monitoring for unauthorized VoIP application use',
    ],
    common_mistakes: 'Allowing employees to use any consumer VoIP application for work calls without governance — creating unmonitored communication channels and potential data leakage.',
    nist_reference: 'SC-19',
  },

  'SC-20': {
    plain_english: 'The system\'s authoritative DNS must provide data origin authentication (DNSSEC signing) with the records it returns to external resolvers.',
    example: 'The organization\'s DNS zones are DNSSEC-signed; all DNS responses include RRSIG records that resolvers can validate to confirm authenticity.',
    evidence_tips: [
      'DNSSEC configuration on authoritative DNS zones',
      'DNS zone signing status verification',
      'Evidence of DNSSEC key management procedures',
    ],
    common_mistakes: 'Running unsigned DNS zones — without DNSSEC, attackers can inject false DNS responses to redirect users to malicious systems (DNS cache poisoning).',
    nist_reference: 'SC-20',
  },

  'SC-21': {
    plain_english: 'The system must validate DNS responses it receives from authoritative sources to ensure they have not been tampered with.',
    example: 'All system DNS resolvers are configured to perform DNSSEC validation; responses that fail validation are rejected and logged as security events.',
    evidence_tips: [
      'DNS resolver configuration showing DNSSEC validation enabled',
      'Evidence of DNSSEC validation failure logging and alerting',
    ],
    common_mistakes: 'Only signing outbound DNS records (SC-20) without enabling validation of inbound DNS responses — leaving the system vulnerable to DNS cache poisoning.',
    nist_reference: 'SC-21',
  },

  'SC-22': {
    plain_english: 'DNS services must be fault-tolerant with multiple geographically distributed authoritative servers and role separation between internal and external resolvers.',
    example: 'External DNS uses two geographically separated authoritative servers; internal DNS uses separate resolvers that do not serve external queries.',
    evidence_tips: [
      'DNS architecture documentation showing redundancy and role separation',
      'Configuration showing separate internal and external DNS resolvers',
      'Failover test records for DNS availability',
    ],
    common_mistakes: 'Using a single DNS server without redundancy — a DNS outage makes the entire system unreachable even if all other components are working.',
    nist_reference: 'SC-22',
  },

  'SC-23': {
    plain_english: 'The system must protect the authenticity of communication sessions to prevent man-in-the-middle attacks and session hijacking.',
    example: 'Session tokens are cryptographically signed, bound to the user\'s IP and user-agent, and expire after 30 minutes; TLS ensures the channel cannot be intercepted.',
    evidence_tips: [
      'Session management implementation documentation',
      'Evidence of session token binding and expiry configuration',
      'Penetration test results confirming session hijacking is not feasible',
    ],
    common_mistakes: 'Using predictable or insufficiently random session tokens that attackers can guess or brute-force to hijack active sessions.',
    nist_reference: 'SC-23',
  },

  'SC-24': {
    plain_english: 'The system must fail to a known secure state — not an undefined or insecure state — when failures occur.',
    example: 'If the authentication service fails, the system denies all access rather than defaulting to open; if the logging service fails, the system alerts and queues events locally.',
    evidence_tips: [
      'Failure mode analysis documenting the expected state for each failure scenario',
      'Architecture documentation showing fail-secure design',
      'Test evidence of fail-secure behavior under simulated failure conditions',
    ],
    common_mistakes: 'Designing systems that "fail open" — defaulting to allowing access when security controls fail — to avoid user disruption at the cost of security.',
    nist_reference: 'SC-24',
  },

  'SC-25': {
    plain_english: 'The organization may employ concealment and deception techniques to confuse adversaries conducting reconnaissance.',
    example: 'Honeypot systems and decoy network paths are deployed to misdirect attacker reconnaissance; real system architecture is obscured in publicly visible configurations.',
    evidence_tips: [
      'Deception technology deployment documentation',
      'Evidence of honeypot or decoy system placement',
      'Alert configuration for interactions with deception assets',
    ],
    common_mistakes: 'Relying solely on perimeter defenses without any deception capabilities — attackers who breach the perimeter conduct reconnaissance unchallenged.',
    nist_reference: 'SC-25',
  },

  'SC-26': {
    plain_english: 'The system should include honeypot components designed to detect attackers or unknown malware by luring them into traps.',
    example: 'Canary tokens are embedded in sensitive documents; fake credentials are placed in a monitored location; any use of these decoys triggers an immediate security alert.',
    evidence_tips: [
      'Honeypot or canary token deployment records',
      'Alert configuration for honeypot interaction events',
      'Evidence of security team response when honeypots are triggered',
    ],
    common_mistakes: 'Deploying honeypots without monitoring — a honeypot that no one watches provides no detection value.',
    nist_reference: 'SC-26',
  },

  'SC-27': {
    plain_english: 'The system should include components that provide layered protection across a broad range of threats to support organizational resilience.',
    example: 'Defense-in-depth architecture includes WAF, IDS/IPS, endpoint protection, network segmentation, and application-layer controls — no single control failure results in a breach.',
    evidence_tips: [
      'Defense-in-depth architecture documentation',
      'Evidence of layered controls at network, host, and application layers',
    ],
    common_mistakes: 'Relying on a single security control (e.g., perimeter firewall) as the primary defense without layered controls that assume perimeter breach.',
    nist_reference: 'SC-27',
  },

  'SC-28': {
    plain_english: 'Information at rest — including databases, file systems, and backups — must be protected by encryption or equivalent controls to prevent unauthorized disclosure.',
    example: 'All S3 buckets use SSE-KMS encryption; RDS databases use AES-256 encryption at rest; backup media is encrypted before transport using a FIPS-validated algorithm.',
    evidence_tips: [
      'Encryption-at-rest configuration for all storage services',
      'KMS key management records',
      'Backup encryption configuration and verification',
    ],
    common_mistakes: 'Encrypting data in transit but not at rest — an attacker who gains storage-level access (e.g., via a misconfigured S3 bucket) can read unencrypted data directly.',
    nist_reference: 'SC-28',
  },

  // ── SYSTEM AND INFORMATION INTEGRITY (SI) ────────────────────────────────

  'SI-1': {
    plain_english: 'You need a written system and information integrity policy covering purpose, scope, roles, and responsibilities, plus procedures for flaw remediation, malware protection, and system monitoring.',
    example: 'An SI Policy is reviewed annually and paired with a Patch Management SOP, Malware Protection SOP, and System Monitoring SOP.',
    evidence_tips: [
      'Current approved SI Policy document',
      'SI procedures documents (patching, malware, monitoring)',
      'Annual review evidence',
    ],
    common_mistakes: 'Managing flaw remediation, malware protection, and monitoring as three separate ad-hoc processes with no unifying policy or defined responsibilities.',
    nist_reference: 'SI-1',
  },

  'SI-2': {
    plain_english: 'The organization must identify, report, and remediate system flaws promptly — with security patches applied within defined timeframes based on severity.',
    example: 'Critical CVEs are patched within 30 days, high within 60 days, and medium within 90 days; patch compliance is measured monthly and reported to the ISSO.',
    evidence_tips: [
      'Vulnerability and patch management policy with defined SLAs by severity',
      'Monthly patch compliance reports',
      'Evidence of patch deployment records with timestamps',
    ],
    common_mistakes: 'Applying patches only during scheduled maintenance windows with no urgency differentiation — critical vulnerabilities being exploited in the wild require emergency patching.',
    nist_reference: 'SI-2',
  },

  'SI-3': {
    plain_english: 'Malicious code protection mechanisms must be deployed at system entry and exit points, kept updated, and configured to scan in real time and on schedule.',
    example: 'Endpoint AV with real-time protection and daily scheduled scans runs on all servers and workstations; signatures are updated automatically every 4 hours.',
    evidence_tips: [
      'AV/EDR deployment scope documentation',
      'Signature update frequency configuration',
      'Scan schedule and most recent scan results',
    ],
    common_mistakes: 'Deploying malware protection on user workstations but not on servers, leaving the most valuable targets unprotected.',
    nist_reference: 'SI-3',
  },

  'SI-4': {
    plain_english: 'The system must be actively monitored to detect attacks, indicators of compromise, and unauthorized use, with findings reported to designated personnel.',
    example: 'A SIEM aggregates logs from all components and applies detection rules; SOC analysts receive real-time alerts for high-severity events and investigate within defined SLAs.',
    evidence_tips: [
      'SIEM deployment and coverage documentation',
      'Detection rule library and alert configuration',
      'SOC alert handling records showing investigation and resolution',
    ],
    common_mistakes: 'Collecting logs in a SIEM but not creating meaningful detection rules — having a SIEM with no tuned alerts is equivalent to having no monitoring.',
    nist_reference: 'SI-4',
  },

  'SI-5': {
    plain_english: 'The organization must receive security alerts and advisories from trusted sources (US-CERT, vendor advisories) and disseminate actionable ones to appropriate personnel.',
    example: 'The ISSO subscribes to CISA alerts and vendor security bulletins; critical advisories are reviewed within 24 hours and assigned to owners for assessment and action.',
    evidence_tips: [
      'Subscription records for CISA alerts and vendor security bulletins',
      'Advisory review and disposition records',
      'Evidence of alert dissemination to relevant technical staff',
    ],
    common_mistakes: 'Subscribing to security alerts but routing them to a shared mailbox that nobody monitors — high-severity advisories are missed until after exploitation.',
    nist_reference: 'SI-5',
  },

  'SI-6': {
    plain_english: 'The system must verify that its security functions are operating correctly, notify personnel when verification fails, and take defined corrective actions.',
    example: 'Automated health checks verify that authentication, encryption, and logging services are functioning every 5 minutes; failures trigger a PagerDuty alert to the on-call engineer.',
    evidence_tips: [
      'Security function health check configuration',
      'Alert rules for security function failures',
      'Records of health check failures and responses',
    ],
    common_mistakes: 'Assuming security controls are working without any active verification — a misconfigured or failed security control can go undetected for extended periods.',
    nist_reference: 'SI-6',
  },

  'SI-7': {
    plain_english: 'The organization must use integrity verification tools to detect unauthorized changes to software, firmware, and critical system files.',
    example: 'A File Integrity Monitoring (FIM) tool monitors critical OS files and application binaries; any unexpected change triggers an alert and automatic ticket creation.',
    evidence_tips: [
      'FIM tool deployment and coverage documentation',
      'Baseline integrity configuration for monitored files',
      'Alert records for unauthorized change detections',
    ],
    common_mistakes: 'Implementing FIM but setting too many false-positive-prone rules that are disabled by operations staff — rendering the control ineffective.',
    nist_reference: 'SI-7',
  },

  'SI-8': {
    plain_english: 'Spam protection must be deployed at email entry and exit points and kept updated to detect and filter unsolicited messages.',
    example: 'An email gateway with anti-spam and anti-phishing filtering processes all inbound mail; signature updates are automatic; quarantined messages are reviewed daily.',
    evidence_tips: [
      'Email gateway spam filtering configuration',
      'Spam filter update frequency configuration',
      'Quarantine review records and spam detection statistics',
    ],
    common_mistakes: 'Relying solely on recipient-side spam filtering without gateway-level controls — server-level filtering catches threats before they reach user mailboxes.',
    nist_reference: 'SI-8',
  },

  'SI-10': {
    plain_english: 'The system must validate all user-supplied input to ensure it conforms to expected formats and rejects or quarantines anything that does not.',
    example: 'All API endpoints validate input against a strict schema; any input exceeding defined length, type, or format constraints is rejected with a 400 error and logged.',
    evidence_tips: [
      'Input validation implementation documentation or code review results',
      'DAST scan results confirming injection vulnerabilities are not present',
      'API schema validation configuration',
    ],
    common_mistakes: 'Relying on client-side input validation only — attackers bypass the browser and send malformed input directly to the API, enabling injection attacks.',
    nist_reference: 'SI-10',
  },

  'SI-11': {
    plain_english: 'Error messages must provide enough information for users to take corrective action without revealing system internals that could help an attacker.',
    example: 'Application errors return generic "An error occurred" messages to users; full stack traces are logged internally but never exposed in the HTTP response.',
    evidence_tips: [
      'Application error handling configuration',
      'Test evidence showing stack traces are not exposed in production',
      'Log review showing detailed errors are captured internally',
    ],
    common_mistakes: 'Displaying full exception stack traces in browser error pages — these reveal system architecture, library versions, and internal paths that attackers use for targeting.',
    nist_reference: 'SI-11',
  },

  'SI-12': {
    plain_english: 'System output must be handled and retained in accordance with applicable regulations, policies, and operational requirements.',
    example: 'Reports containing PII are treated as sensitive output, watermarked with the recipient\'s name, and retained for 3 years per records management policy.',
    evidence_tips: [
      'Output handling policy defining retention and protection requirements',
      'Evidence that sensitive output is labeled and controlled',
      'Retention schedule aligned with regulatory requirements',
    ],
    common_mistakes: 'Treating printed or exported system output as lower-sensitivity than the source data — output often contains the same sensitive information.',
    nist_reference: 'SI-12',
  },

  'SI-13': {
    plain_english: 'The system must be able to fail over to a secondary system or backup in near-real time to maintain continuity of critical operations.',
    example: 'The application runs active-active across two availability zones; an automatic failover routes traffic to the healthy zone within 30 seconds of a zone failure.',
    evidence_tips: [
      'High-availability architecture documentation',
      'Failover test records showing actual failover time',
      'Monitoring configuration for failover events',
    ],
    common_mistakes: 'Designing a hot standby that requires manual intervention to activate — human response time adds minutes or hours to the actual failover time.',
    nist_reference: 'SI-13',
  },

  'SI-14': {
    plain_english: 'Defined system components should use non-persistent operating environments (e.g., containers that are destroyed after each use) to reduce the risk of persistent malware.',
    example: 'Lambda functions and short-lived containers are used for sensitive processing tasks; each invocation runs in a fresh environment, preventing persistence between executions.',
    evidence_tips: [
      'Architecture documentation identifying non-persistent component use cases',
      'Container or serverless configuration confirming fresh environments per invocation',
    ],
    common_mistakes: 'Using long-running persistent VMs for sensitive workloads when ephemeral containers could eliminate entire classes of persistence-based attacks.',
    nist_reference: 'SI-14',
  },

  'SI-15': {
    plain_english: 'The system must validate output to ensure it is appropriate in content and format before distributing it to recipients.',
    example: 'Report generation validates that output contains only fields the requesting user is authorized to see, and rejects generation if data exceeds authorized scope.',
    evidence_tips: [
      'Output validation logic documentation or code review',
      'Evidence of authorization checks before output generation',
      'Test results confirming unauthorized data cannot appear in system output',
    ],
    common_mistakes: 'Validating input rigorously while ignoring output validation — over-fetching or encoding errors can expose sensitive data in output even when input is clean.',
    nist_reference: 'SI-15',
  },

  'SI-16': {
    plain_english: 'The system must implement memory protection mechanisms — such as DEP and ASLR — to prevent exploitation through memory corruption vulnerabilities.',
    example: 'All servers have Data Execution Prevention (DEP) and Address Space Layout Randomization (ASLR) enabled at the OS level; application binaries are compiled with stack protection flags.',
    evidence_tips: [
      'OS configuration showing DEP and ASLR are enabled',
      'Compiler security flags in the build pipeline configuration',
      'Penetration test results confirming memory protections are effective',
    ],
    common_mistakes: 'Using legacy operating systems or compilers that do not support modern memory protections, leaving the system vulnerable to buffer overflow exploitation.',
    nist_reference: 'SI-16',
  },

  'SI-17': {
    plain_english: 'The system must detect and overwrite or clear sensitive information from data structures after processes complete to prevent residual data disclosure.',
    example: 'Temporary files created during document processing are securely deleted after the task completes; memory buffers holding credentials are zeroed after use.',
    evidence_tips: [
      'Application design documentation describing residual data handling',
      'Code review confirming sensitive data is cleared after use',
      'Test evidence showing sensitive residual data cannot be recovered from completed processes',
    ],
    common_mistakes: 'Relying on garbage collection or OS memory management to clear sensitive data — these mechanisms do not guarantee timely or complete erasure.',
    nist_reference: 'SI-17',
  },

  'SI-18': {
    plain_english: 'When processing PII for secondary purposes like testing or research, de-identification techniques must be applied to protect individual privacy.',
    example: 'Production data used for testing is run through a de-identification pipeline that masks names, SSNs, and email addresses before loading into the test environment.',
    evidence_tips: [
      'De-identification policy and approved techniques documentation',
      'Test environment data pipeline configuration showing de-identification steps',
      'Evidence that production PII never appears in non-production environments',
    ],
    common_mistakes: 'Copying production data with real PII into test or development environments where it is subject to weaker controls and broader access.',
    nist_reference: 'SI-18',
  },

  'SI-19': {
    plain_english: 'The organization must minimize the PII it collects and processes, removing it from datasets when it is no longer necessary.',
    example: 'A PII data minimization review is conducted quarterly; fields collected but no longer needed for a process are removed from the data model and purged from existing records.',
    evidence_tips: [
      'Data minimization policy and review records',
      'Evidence of unnecessary PII removal from datasets',
      'Data inventory showing PII fields and their business justification',
    ],
    common_mistakes: 'Collecting PII "in case it might be useful later" without a defined purpose — unnecessary PII creates liability with no corresponding benefit.',
    nist_reference: 'SI-19',
  },

  'SI-20': {
    plain_english: 'The organization must maintain information integrity throughout the system lifecycle — from creation through disposal — preserving fidelity and accuracy.',
    example: 'Hash verification is performed when data is imported, exported, or migrated; integrity checks are run on archived records before disposal to confirm completeness.',
    evidence_tips: [
      'Data integrity verification procedures',
      'Hash or checksum records for data imports, exports, and migrations',
      'Evidence of integrity checks before data disposal',
    ],
    common_mistakes: 'Assuming data integrity is maintained without any verification — silent data corruption can go undetected for long periods without checksums.',
    nist_reference: 'SI-20',
  },

  'SI-21': {
    plain_english: 'Defined personnel must validate information inputs from authorized sources before the system processes them, adding a human verification step for high-stakes inputs.',
    example: 'Financial transaction imports over a defined threshold require a second analyst to verify the source file integrity before processing.',
    evidence_tips: [
      'Input validation procedures requiring human review for defined input types',
      'Records of human validation steps for authorized inputs',
    ],
    common_mistakes: 'Automating all input processing without any human verification for high-value or high-risk input sources where a compromised upstream could cause significant harm.',
    nist_reference: 'SI-21',
  },

  'SI-22': {
    plain_english: 'The organization must identify and address publicly known vulnerabilities by incorporating threat intelligence into patching and risk management processes.',
    example: 'The vulnerability management team monitors NVD and CISA KEV daily; vulnerabilities in the CISA KEV catalog are prioritized for immediate remediation regardless of CVSS score.',
    evidence_tips: [
      'Evidence of NVD/CISA KEV monitoring process',
      'Remediation records showing CISA KEV vulnerabilities are prioritized',
      'Threat intelligence integration with the patching workflow',
    ],
    common_mistakes: 'Prioritizing patching solely by CVSS score without considering whether a vulnerability is being actively exploited in the wild.',
    nist_reference: 'SI-22',
  },

  'SI-23': {
    plain_english: 'The organization must verify the integrity of the system and its components by comparing measurements to known-good baselines and taking corrective action on anomalies.',
    example: 'A configuration compliance tool compares running system configurations to the approved baseline daily; deviations trigger a ticket and must be remediated or approved within 48 hours.',
    evidence_tips: [
      'Integrity baseline configuration and measurement tool deployment',
      'Daily or periodic integrity comparison reports',
      'Remediation records for detected integrity anomalies',
    ],
    common_mistakes: 'Creating a baseline once but never comparing running systems against it — configuration drift accumulates invisibly and is discovered only during an incident or audit.',
    nist_reference: 'SI-23',
  },

  'SI-4(2)': {
    plain_english: 'Automated tools must support near-real-time analysis of security events to detect attacks and indicators of compromise as they happen.',
    example: 'A SIEM with automated correlation rules analyzes events in real time; a machine-learning-based UEBA tool detects behavioral anomalies within seconds of occurrence.',
    evidence_tips: [
      'SIEM real-time alerting configuration',
      'UEBA or automated detection tool deployment documentation',
      'Alert latency metrics showing near-real-time detection capability',
    ],
    common_mistakes: 'Collecting logs centrally but processing them only in nightly batch jobs — attacks that take minutes to complete are missed entirely by delayed analysis.',
    nist_reference: 'SI-4(2)',
  },

  // ── SUPPLY CHAIN RISK MANAGEMENT (SR) ────────────────────────────────────

  'SR-1': {
    plain_english: 'You need a written supply chain risk management policy covering purpose, scope, roles, and responsibilities, plus procedures for how supply chain risks are identified, assessed, and mitigated.',
    example: 'An SR Policy is reviewed annually and paired with an SCRM SOP describing supplier vetting requirements, component provenance checks, and prohibited-supplier criteria.',
    evidence_tips: [
      'Current approved SR Policy document',
      'Supply chain risk management procedures document',
      'Annual review evidence',
    ],
    common_mistakes: 'Treating supply chain risk as a procurement issue only — without a security-focused SCRM policy, critical components may be acquired from untrusted sources.',
    nist_reference: 'SR-1',
  },

  'SR-2': {
    plain_english: 'The organization must develop a formal Supply Chain Risk Management Plan that addresses how risks from suppliers and components are identified and managed throughout the system lifecycle.',
    example: 'An SCRM Plan documents approved supplier tiers, risk assessment methodology for new vendors, prohibited countries of origin, and incident response procedures for supply chain compromises.',
    evidence_tips: [
      'Supply Chain Risk Management Plan with current approval date',
      'Evidence of annual plan review and update',
      'Stakeholder sign-off showing leadership awareness of supply chain risks',
    ],
    common_mistakes: 'Conflating the SCRM Plan with general vendor management processes — SCRM specifically addresses security risks from hardware, software, and service supply chains.',
    nist_reference: 'SR-2',
  },

  'SR-3': {
    plain_english: 'Supply chain controls and security requirements must be established in contracts with suppliers and sub-tier suppliers, reviewed, and updated as threats evolve.',
    example: 'All hardware and software procurement contracts include a Supply Chain Security Appendix requiring provenance documentation, security testing evidence, and notification of any supply chain incidents.',
    evidence_tips: [
      'Contract templates with supply chain security requirements language',
      'Signed supplier contracts showing SCRM requirements included',
      'Evidence of periodic contract review to ensure requirements remain current',
    ],
    common_mistakes: 'Applying supply chain controls only to direct suppliers without flowing requirements down to sub-tier suppliers — vulnerabilities can be introduced at any tier.',
    nist_reference: 'SR-3',
  },

  'SR-4': {
    plain_english: 'The organization must maintain an inventory tracking the provenance and integrity of system components throughout their lifecycle — from acquisition through disposal.',
    example: 'A component inventory records each hardware item\'s manufacturer, country of origin, serial number, and acquisition source; software components are tracked via a Software Bill of Materials (SBOM).',
    evidence_tips: [
      'Component inventory or CMDB with provenance fields populated',
      'Software Bill of Materials (SBOM) for critical applications',
      'Evidence of inventory updates when components are added or removed',
    ],
    common_mistakes: 'Tracking assets for inventory purposes but not recording provenance information — without knowing where components came from, supply chain risk cannot be assessed.',
    nist_reference: 'SR-4',
  },

  'SR-5': {
    plain_english: 'The organization must use acquisition strategies and procurement methods that protect against supply chain threats and minimize attack surface from components.',
    example: 'Procurement policy requires purchasing from the original equipment manufacturer or authorized distributors; gray market sources are prohibited; all acquisitions require a SCRM review.',
    evidence_tips: [
      'Procurement policy referencing supply chain security requirements',
      'Approved supplier list and authorization criteria',
      'SCRM review records for significant component acquisitions',
    ],
    common_mistakes: 'Sourcing from lowest-cost suppliers without verifying their position in the authorized distribution chain — gray market components may be counterfeit or tampered.',
    nist_reference: 'SR-5',
  },

  'SR-6': {
    plain_english: 'Suppliers and third-party providers must be assessed regularly using defined criteria to verify their ongoing supply chain security posture.',
    example: 'Annual supplier assessments use a standardized questionnaire covering their security controls, subcontractor management, and incident history; high-risk suppliers receive on-site assessments.',
    evidence_tips: [
      'Supplier assessment methodology and criteria documentation',
      'Annual supplier assessment records',
      'Corrective action plans for suppliers with identified gaps',
    ],
    common_mistakes: 'Vetting suppliers only at contract signing and never reassessing — a supplier\'s security posture can degrade significantly over a multi-year relationship.',
    nist_reference: 'SR-6',
  },

  'SR-7': {
    plain_english: 'The organization must employ techniques to prevent, detect, and inspect for counterfeit components entering the supply chain.',
    example: 'Incoming hardware components are inspected against manufacturer specifications and verified with the supplier\'s authenticity portal; serial numbers are validated before deployment.',
    evidence_tips: [
      'Counterfeit detection procedures and inspection records',
      'Manufacturer authenticity verification process documentation',
      'Training records for staff performing component inspection',
    ],
    common_mistakes: 'Deploying hardware without authenticity verification — counterfeit components may contain backdoors or fail prematurely under load, causing outages or breaches.',
    nist_reference: 'SR-7',
  },

  'SR-8': {
    plain_english: 'The organization must have a process to report and respond to counterfeit or unauthorized components, including notifying appropriate personnel and authorities.',
    example: 'When a counterfeit component is identified, the ISSO is notified immediately, the component is quarantined, the supplier is notified for replacement, and the incident is reported to US-CERT.',
    evidence_tips: [
      'Counterfeit component reporting procedures',
      'Records of any counterfeit component discoveries and notifications',
      'Escalation contacts for counterfeit reporting (supplier, US-CERT)',
    ],
    common_mistakes: 'Quietly replacing a confirmed counterfeit component without reporting it — unreported counterfeits allow the same components to reach other organizations.',
    nist_reference: 'SR-8',
  },

  'SR-9': {
    plain_english: 'The organization must employ anti-tamper technologies and techniques to protect systems and components from physical tampering throughout their lifecycle.',
    example: 'Critical hardware uses tamper-evident seals; chassis intrusion detection alerts when cases are opened; tamper-evident labels are verified during each maintenance session.',
    evidence_tips: [
      'Anti-tamper technology deployment documentation',
      'Tamper-evident seal inspection records',
      'Chassis intrusion detection configuration and alert records',
    ],
    common_mistakes: 'Relying solely on logical security controls without physical tamper protection — an adversary with brief physical access can install persistent hardware implants.',
    nist_reference: 'SR-9',
  },

  'SR-10': {
    plain_english: 'The organization must conduct inspections of systems, components, and services at defined intervals and upon triggering events to detect supply chain compromises.',
    example: 'Quarterly component inspections verify tamper-evident seals and compare installed firmware versions against vendor-published baselines; unscheduled inspections occur after any suspicious incident.',
    evidence_tips: [
      'Component inspection schedule and completed inspection records',
      'Defined triggering events that prompt unscheduled inspections',
      'Inspection checklists and findings reports',
    ],
    common_mistakes: 'Conducting inspections only at initial receipt without follow-up inspections throughout the operational lifecycle — compromise can occur after deployment.',
    nist_reference: 'SR-10',
  },

  'SR-11': {
    plain_english: 'The organization must verify the authenticity of system components to detect counterfeits and notify designated personnel when counterfeit components are found.',
    example: 'Network equipment firmware is verified against manufacturer-published hashes before installation; the security team is notified immediately if verification fails.',
    evidence_tips: [
      'Component authenticity verification procedures and tools',
      'Firmware/software hash verification records',
      'Notification records for any failed authenticity verifications',
    ],
    common_mistakes: 'Installing firmware or software updates without verifying their integrity against manufacturer-published checksums — tampered updates are a primary nation-state attack vector.',
    nist_reference: 'SR-11',
  },

  'SR-12': {
    plain_english: 'Systems and components must be disposed of in accordance with applicable laws, regulations, and organizational requirements to prevent data recovery and unauthorized reuse.',
    example: 'Decommissioned hardware is sanitized per NIST SP 800-88 before disposal; a certificate of destruction is obtained from the disposal vendor and retained for 5 years.',
    evidence_tips: [
      'Component disposal policy referencing NIST SP 800-88 or equivalent',
      'Certificates of destruction or sanitization records',
      'Disposal vendor contracts specifying secure disposal methods',
    ],
    common_mistakes: 'Returning leased hardware to vendors without first sanitizing storage media — vendors routinely reuse or resell decommissioned equipment, potentially exposing organizational data.',
    nist_reference: 'SR-12',
  },

};

export default FEDRAMP_EXPLANATIONS;
